import { findAddressMatches } from "./duplicateAddressCheck";
import { selectPendingFileRequests } from "./fileRequestQueue";
import {
  collection,
  doc,
  getDocs,
  query,
  orderBy,
  arrayUnion,
  updateDoc,
  getDoc,
  where,
  onSnapshot,
  setDoc,
  deleteDoc,
  addDoc,
  limit,
  startAfter,
  getCountFromServer,
  runTransaction,
  serverTimestamp,
  increment,
  writeBatch,
} from "firebase/firestore";
import { firestored, storage } from "../firebase/firebaseConfig";
import { authedFetch } from "./authedFetch";
import { getDealsToAutoArchive } from "./autoArchive";
import { normalizePro, isLoggablePro } from "./teamDirectory";
import { listClients } from "./leadClients";
import { mergeClientIds } from "./intakeLead";
import { planStatusCleanup } from "./statusSheetData";
import {
  auditDealsForGuessedClosingDates,
  looksLikeGuessedClosingDate,
} from "./closingDateAudit";
import { planDuplicateMerge, computeMerge } from "./duplicateLeads";
import { ensureLeadOwner } from "./leadOwnership";
import { canSearchTeamContacts } from "./roles";
import { tagTeamContacts } from "./teamContacts";
import {
  buildUpForGrabsPostText,
  buildUpForGrabsFilledText,
  buildUpForGrabsCancelledText,
} from "./upForGrabsFeed";
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import {
  computeNewReactionState,
  applyReactionDeltas,
  EMPTY_REACTION_COUNTS,
} from "./feedHelpers";
import { generateFeedbackToken } from "./openHouseHelpers";
import { findAgentCoordinatorTasks, buildDealBackfill, ACTIVE_STAGES, STAGE_ORDER } from "./taskSections";
import { applyFileRequestDetails } from "./fileRequestDetails";
import { findAppointmentForLead } from "./appointmentMatch";
import { buildDealFromLead } from "./buildDealFromLead";
import { buildBuyerNeedPost } from "./buyerNeedPost";
import {
  upsertRequest,
  applyWinner,
  declineAll,
} from "./openHouseOpportunityHelpers";

const auth = getAuth();

export const saveuserdata = async (data, uid) => {
  return await setDoc(doc(firestored, "users", `${uid}`), data, { merge: true })
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

export const userlogin = async (email, password) => {
  return await signInWithEmailAndPassword(auth, email, password)
    .then((userCredential) => {
      // Signed in
      const user = userCredential.user;
      return user;
      // ...
    })
    .catch((err) => {
      return err;
    });
};

// Soft-archive a deal: hidden from the board by default; restore by setting false.
export const setDealArchived = async (dealId, archived) => {
  const docRef = doc(firestored, "deals", String(dealId));
  await setDoc(docRef, { archived: !!archived }, { merge: true });
  return true;
};

// Soft delete: mark the deal as deleted with a timestamp. Kept for 30 days
// in the Recently Deleted view, then can be permanently purged. Photos are
// preserved so a restore brings the deal back intact.
export const deleteDealWithPhotos = async (dealId) => {
  try {
    const docRef = doc(firestored, "deals", String(dealId));
    await setDoc(docRef, { deletedAt: Date.now() }, { merge: true });
    return true;
  } catch (error) {
    console.error("🚨 Error soft-deleting deal:", error);
    return false;
  }
};

// Clear the deletedAt flag — brings a soft-deleted deal back into the main list.
export const restoreDeal = async (dealId) => {
  try {
    const docRef = doc(firestored, "deals", String(dealId));
    // Firestore can't unset via setDoc merge with undefined — write null and
    // treat null/0/missing as "not deleted" in the read filters.
    await setDoc(docRef, { deletedAt: null }, { merge: true });
    return true;
  } catch (error) {
    console.error("🚨 Error restoring deal:", error);
    return false;
  }
};

// Permanent destroy — used by the Recently Deleted view when the user clicks
// "Delete forever" or by the 30-day auto-purge. This is the original
// destructive behavior.
export const permanentlyDeleteDeal = async (dealId) => {
  try {
    const dealsCollection = collection(firestored, "deals");
    const q = query(dealsCollection, where("createdat", "==", dealId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) return false;

    for (const dealDoc of querySnapshot.docs) {
      const dealData = dealDoc.data();
      if (Array.isArray(dealData.propertyphoto)) {
        for (const photoUrl of dealData.propertyphoto) {
          try {
            await deleteObject(ref(storage, photoUrl));
          } catch (imgErr) {
            console.warn("⚠️ Image delete failed:", photoUrl, imgErr);
          }
        }
      }
      await deleteDoc(doc(firestored, "deals", dealDoc.id));
    }
    return true;
  } catch (error) {
    console.error("🚨 Error permanently deleting deal:", error);
    return false;
  }
};

// Return only deals that have been soft-deleted (deletedAt is a number > 0).
export const getDeletedDeals = async () => {
  const items = await getDocs(collection(firestored, "deals"));
  return items.docs
    .map((d) => d.data())
    .filter((d) => typeof d.deletedAt === "number" && d.deletedAt > 0)
    .sort((a, b) => (b.deletedAt || 0) - (a.deletedAt || 0));
};

export const fetchUserById = async (id) => {
  try {
    console.log(`🔍 Fetching blog with user id: ${id}`);

    const blogsCollection = collection(firestored, "users");
    const q = query(blogsCollection, where("id", "==", id)); // Filter blogsId
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log("❌ No such document in Firestore!");
      return null;
    }

    let userData = null;
    querySnapshot.forEach((doc) => {
      userData = doc.data();
    });

    console.log("✅ Blog found:", userData);
    return userData;
  } catch (error) {
    console.error("🚨 Error fetching blog:", error);
    return null;
  }
};

export const updateUserProfile = async (userId, updatedData) => {
  try {
    const userRef = doc(firestored, "users", userId);
    await updateDoc(userRef, updatedData);
    return true;
  } catch (error) {
    console.error("Error updating profile:", error);
    return false;
  }
};

// ---- Follow Up Boss integration (single team-wide connection) --------------
// Stored in ONE doc (integrations/followUpBoss) so the backend (functions-fub)
// reads it without scanning users. The owner connects once with the owner API
// key, which covers the whole shared FUB account.
export const getFubIntegration = async () => {
  try {
    const snap = await getDoc(doc(firestored, "integrations", "followUpBoss"));
    return snap.exists() ? snap.data() : null;
  } catch (error) {
    console.error("Error reading Follow Up Boss integration:", error);
    return null;
  }
};

export const saveFubIntegration = async (data) => {
  try {
    await setDoc(doc(firestored, "integrations", "followUpBoss"), data, {
      merge: true,
    });
    return true;
  } catch (error) {
    console.error("Error saving Follow Up Boss integration:", error);
    return false;
  }
};

// Base URL for the Follow Up Boss sync Cloud Functions (isolated "fub" codebase).
const FUB_FUNCTIONS_BASE =
  "https://us-central1-team-edelstein.cloudfunctions.net";

// Fire-and-forget: push a lead to Follow Up Boss after it's saved/changed. The
// endpoint no-ops when sync is disconnected, so it's always safe to call, and a
// failure here never breaks the save.
export const pushLeadToFub = (leadId, type) => {
  if (!leadId) return;
  try {
    authedFetch(`${FUB_FUNCTIONS_BASE}/fubPush`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ leadId, type }),
    }).catch(() => {});
  } catch (_) {
    /* ignore */
  }
};

// One-time "Activate Live Sync": asks the backend to register the FUB webhooks.
export const registerFubWebhooks = async () => {
  try {
    const res = await authedFetch(`${FUB_FUNCTIONS_BASE}/fubRegisterWebhooks`, {
      method: "POST",
    });
    return await res.json().catch(() => ({ ok: false }));
  } catch (e) {
    return { ok: false, error: String(e) };
  }
};

// "Import from Follow Up Boss" (Contacts page): pulls FUB people carrying the
// configured Sync Tag into sync_contacts for this user, mirroring the Google
// "Sync Contacts" button. Throws on failure so the caller can surface the
// message (e.g. "not connected"). Returns { ok, imported, scanned, tag }.
export const importFubContacts = async (userId) => {
  const res = await authedFetch(`${FUB_FUNCTIONS_BASE}/fubImportContacts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Import failed");
  }
  return res.json();
};

// Pick-and-choose import: list ALL Follow Up Boss contacts for preview (no tag
// gate, no writes), then import only the selected ones (de-duped on the
// backend via fub_<id>). Both throw on failure so the modal can surface it.
export const listFubContacts = async () => {
  const res = await authedFetch(`${FUB_FUNCTIONS_BASE}/fubListContacts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Could not load Follow Up Boss contacts");
  }
  return res.json(); // { ok, contacts, total }
};

export const importFubSelected = async (userId, people) => {
  const res = await authedFetch(`${FUB_FUNCTIONS_BASE}/fubImportSelected`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId, people }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Import failed");
  }
  return res.json(); // { ok, imported }
};

export const updateAccessUser = async (userId, updatedData) => {
  try {
    const userRef = doc(firestored, "users", userId);
    await updateDoc(userRef, updatedData);
    return true;
  } catch (error) {
    console.error("Error updating profile:", error);
    return false;
  }
};

export const changeUserPassword = async (
  email,
  currentPassword,
  newPassword,
) => {
  const auth = getAuth();
  const user = auth.currentUser;

  // Create credential
  const credential = EmailAuthProvider.credential(email, currentPassword);

  // Step 1: Re-authenticate
  await reauthenticateWithCredential(user, credential);

  // Step 2: Update password
  await updatePassword(user, newPassword);
};

export const uploadUserImage = async (file, userId) => {
  try {
    const storageRef = ref(storage, `profilePictures/${userId}/${file.name}`);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error("Error uploading image:", error);
    return null;
  }
};

export const getUser = async () => {
  try {
    const querySnapshot = await getDocs(collection(firestored, "users"));
    const data = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    console.log("users data", data);
    return data;
  } catch (error) {
    console.error("Error fetching users: ", error);
    return [];
  }
};

export const getHelpElker = async () => {
  try {
    const querySnapshot = await getDocs(collection(firestored, "helpElker"));
    const data = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    console.log("helpElker data", data);
    return data;
  } catch (error) {
    console.error("Error fetching helpElker: ", error);
    return [];
  }
};

// export const getuserinformation = async (collection, uid) => {
//   try {
//     const userRef = ref(firestored, `${collection}/${uid}`);
//     const snapshot = await get(userRef);

//     if (snapshot.exists()) {
//       console.log("User exists in Firebase:", snapshot.val());
//       return snapshot.val();
//     } else {
//       console.warn("User not found in Firebase!");
//       return null;
//     }
//   } catch (error) {
//     console.error("Error fetching user data:", error);
//     return null;
//   }
// };

export const getuserinformation = async (collectionname, documentId) => {
  try {
    const docRef = doc(firestored, collectionname, documentId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      console.log("User Data:", docSnap.data());
      return docSnap.data();
    } else {
      console.log("No such document!");
      return null;
    }
  } catch (error) {
    console.error("Error fetching document:", error);
    return null;
  }
};

// The Gmail refresh/access token now lives in a private subdoc
// (users/{uid}/private/gmail) so it is NOT exposed inside the broadly-readable
// users doc. Returns the token object, or null if Gmail isn't connected (or the
// read is blocked). Callers merge this into the in-memory user so every
// `user.gmailtoken.access_token` check keeps working unchanged.
export const getUserGmailToken = async (uid) => {
  if (!uid) return null;
  try {
    const snap = await getDoc(doc(firestored, "users", uid, "private", "gmail"));
    return snap.exists() ? snap.data()?.gmailtoken || null : null;
  } catch (error) {
    console.error("Error fetching Gmail token:", error);
    return null;
  }
};

// Loads a user's record AND merges in their Gmail token from the private
// subdoc. Falls back to the legacy users.gmailtoken field for any account not
// yet migrated. Use this everywhere the current user is (re)loaded into state,
// so the Gmail connection survives every profile reload.
export const loadCurrentUser = async (uid) => {
  const userInfo = await getuserinformation("users", uid);
  if (!userInfo) return null;
  const priv = await getUserGmailToken(uid);
  return { ...userInfo, gmailtoken: priv || userInfo.gmailtoken || {} };
};

// Clears the Gmail token in the private subdoc (the disconnect flow also clears
// the legacy users doc fields via updateUserProfile).
export const clearUserGmailToken = async (uid) => {
  if (!uid) return;
  try {
    await setDoc(
      doc(firestored, "users", uid, "private", "gmail"),
      { gmailtoken: {} },
      { merge: true },
    );
  } catch (error) {
    console.error("Error clearing Gmail token:", error);
  }
};

export const getAllUserItems = async (table, parameter, comparison, value) => {
  const items = await getDocs(
    query(collection(firestored, table), where(parameter, comparison, value)),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllSlotsWhere = async (
  table,
  parameter,
  comparison,
  value,
  parameter1,
  comparison1,
  value1,
  parameter2,
  comparison2,
  value2,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, comparison, value),
      where(parameter1, comparison1, value1),
      where(parameter2, comparison2, value2),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllSlotsWherex4 = async (
  table,
  parameter,
  comparison,
  value,
  parameter1,
  comparison1,
  value1,
  parameter2,
  comparison2,
  value2,
  parameter3,
  comparison3,
  value3,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, comparison, value),
      where(parameter1, comparison1, value1),
      where(parameter2, comparison2, value2),
      where(parameter3, comparison3, value3),
    ),
  );

  return items.docs.map((doc) => doc.data());
};
export const getAllSlotsWherex3 = async (
  table,
  parameter,
  comparison,
  value,
  parameter1,
  comparison1,
  value1,
  parameter2,
  comparison2,
  value2,
  parameter3,
  comparison3,
  value3,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, comparison, value),
      where(parameter1, comparison1, value1),
      where(parameter1, comparison1, value1),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllSlotsForChecking = async (
  table,
  parameter,
  comparison,
  value,
  parameter1,
  comparison1,
  value1,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, comparison, value),
      where(parameter1, comparison1, value1),
      where("status", "==", "Accepted"),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllEmployeeandSupervisors = async (
  table,
  parameter,
  comparison,
  value,
  value2,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, "in", [value, value2]),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllEmployeeandSupervisorsforclient = async (
  table,
  parameter,
  comparison,
  value,
  value2,
  clientid,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, "in", [value, value2]),
      where("createdby", "==", clientid),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllApprovedleaves = async (table, parameter, data) => {
  const items = await getDocs(
    query(collection(firestored, table), where(parameter, "in", data)),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllCleanerShifts = async (table, cleanerid, date) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where("cleaningshiftid", "==", cleanerid),
      where("sheduleddate", "==", date),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllEmployeeandSupervisorsforsupervisor = async (
  table,
  parameter,
  comparison,
  value,
  value2,
  creatorid,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, "in", [value, value2]),
      where("createdby", "==", creatorid),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllEmployeeForProject = async (
  table,
  parameter,
  comparison,
  value,
  projectid,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, comparison, value),
      where("projectassigned", comparison, projectid),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const getAllEmployeeForClient = async (
  table,
  parameter,
  comparison,
  value,
  projectid,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, comparison, value),
      where("createdby", comparison, projectid),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const uploadImagesToFirebase = async (files) => {
  const uploadedUrls = [];

  for (let file of files) {
    const fileName = `dealPhotos/${Date.now()}-${file.name}`;
    const storageRef = ref(storage, fileName);

    const uploadTask = await uploadBytesResumable(storageRef, file);

    const downloadUrl = await getDownloadURL(uploadTask.ref);
    uploadedUrls.push(downloadUrl);
  }

  return uploadedUrls; // <-- array of URLs
};

export const saveitemwithid = async (table, id, data) => {
  return await setDoc(doc(firestored, table, id), data, { merge: true })
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

export const getDealById = async (id) => {
  try {
    const dealRef = doc(firestored, "deals", id);
    const dealSnap = await getDoc(dealRef);

    if (dealSnap.exists()) {
      return { id: dealSnap.id, ...dealSnap.data() };
    } else {
      throw new Error("recipe not found");
    }
  } catch (error) {
    console.error("Error fetching recipe:", error);
    throw error;
  }
};

// Is there actually a deal document at this id? getDealById throws for BOTH
// "absent" and "couldn't read it", which callers deciding whether to RESTORE a
// deal must tell apart: updateDealById writes with setDoc(merge), so patching
// an absent deal CREATES one holding nothing but the patch. That is how a
// graduated listing lead landed on the board with no address, no client and no
// leadsource while the lead itself was marked converted and vanished from the
// pipeline (2026-08-07, 96 Orange Ave W -> dangling sourceDealId 1783354227389).
// Deliberately does NOT swallow read errors: a caller that cannot tell whether
// the deal is there must fail loudly rather than invent one.
export const dealExists = async (dealId) => {
  const snap = await getDoc(doc(firestored, "deals", String(dealId)));
  return snap.exists();
};

export const updateItem = async (table, id, data) => {
  return await setDoc(doc(firestored, table, id.toString()), data, {
    merge: true,
  })
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

export const updateNestedItem = async (
  table,
  id,
  innertable,
  data,
  innerid,
) => {
  return await setDoc(
    doc(firestored, `${table}/${id}/${innertable}`, innerid.toString()),
    data,
    { merge: true },
  )
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};
export const updateNestedArrayItem = async (
  table,
  id,
  innertable,
  data,
  innerid,
) => {
  return await setDoc(
    doc(firestored, `${table}/${id}/${innertable}`, innerid.toString()),
    { slots: arrayUnion(data) },
    { merge: true },
  )
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

export const resetpassword = async (email) => {
  return await sendPasswordResetEmail(auth, email)
    .then((response) => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

// ✅ Save Lead Source
export const saveLeadSource = async (leadData) => {
  try {
    const docRef = doc(collection(firestored, "lead_sources"));

    await setDoc(docRef, {
      docId: docRef.id,
      abbreviation: leadData.abbreviation,
      name: leadData.name,
      leadCommission: leadData.leadCommission
        ? Number(leadData.leadCommission)
        : 0,
      createdAt: Date.now(),
    });

    return { success: true, id: docRef.id }; // ✅ success only after Firestore commit
  } catch (error) {
    console.error("Error saving lead source:", error);
    return { success: false, error: error.message };
  }
};

export const updateLeadSource = async (id, data) => {
  try {
    // Fetch existing document data
    const docRef = doc(firestored, "lead_sources", id);

    await updateDoc(docRef, data);
    return { success: true, id: id };
  } catch (error) {
    console.error("Error updating leade:", error);
    return { success: false, error: error.message };
  }
};

// ✅ Delete Lead Source by ID
export const deleteLeadSource = async (id) => {
  try {
    await deleteDoc(doc(firestored, "lead_sources", id)); // ✅ wait for Firestore delete
    return { success: true };
  } catch (error) {
    console.error("Error deleting lead source:", error);
    return { success: false, error: error.message };
  }
};

// ---- Buyer Leads -----------------------------------------------------------
const BUYER_LEADS_COLLECTION = "buyer_leads";

// Create a buyer lead with an auto-generated id. Stamps id/timestamps/defaults.
export const saveBuyerLead = async (data, opts = {}) => {
  try {
    const ref = doc(collection(firestored, BUYER_LEADS_COLLECTION));
    const now = Date.now();
    const lead = {
      ...ensureLeadOwner(data),
      id: ref.id,
      status: data.status || "active",
      stage: data.stage || "New Lead",
      createdAt: now,
      updatedAt: now,
      deletedAt: 0,
    };
    await setDoc(ref, lead);
    // Opt-in sync: a brand-new lead only pushes when its "Sync to Follow Up
    // Boss" switch was flipped on. (Updates still call the backend, which
    // makes the same check against the stored lead.)
    if (!opts.skipFubPush && lead.fubSync === true) pushLeadToFub(ref.id, "buyer");
    return { success: true, id: ref.id, lead };
  } catch (error) {
    console.error("Error saving buyer lead:", error);
    return { success: false, error: error.message };
  }
};

export const updateBuyerLead = async (id, patch, opts = {}) => {
  try {
    await updateDoc(doc(firestored, BUYER_LEADS_COLLECTION, id), {
      ...patch,
      updatedAt: Date.now(),
    });
    if (!opts.skipFubPush) pushLeadToFub(id, "buyer");
    return { success: true, id };
  } catch (error) {
    console.error("Error updating buyer lead:", error);
    return { success: false, error: error.message };
  }
};

// Mirrors updateDealStageInFirebase — used by the board's drag-and-drop.
export const updateBuyerLeadStage = async (id, newStage, extra = {}) =>
  updateBuyerLead(id, { stage: newStage, ...extra });

// Soft delete (mirrors the deals `deletedAt` convention).
export const deleteBuyerLead = async (id) => {
  try {
    await updateDoc(doc(firestored, BUYER_LEADS_COLLECTION, id), {
      deletedAt: Date.now(),
    });
    return { success: true };
  } catch (error) {
    console.error("Error deleting buyer lead:", error);
    return { success: false, error: error.message };
  }
};

// Real-time list (mirrors listenLeadSources). Filters out soft-deleted rows.
export const listenBuyerLeads = (callback) => {
  const q = query(
    collection(firestored, BUYER_LEADS_COLLECTION),
    orderBy("createdAt", "desc"),
  );
  return onSnapshot(q, (snap) => {
    const rows = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((l) => !(Number(l.deletedAt) > 0));
    callback(rows);
  });
};

// ---- Listing (seller) leads — mirrors the buyer_leads helpers above ----
const LISTING_LEADS_COLLECTION = "listing_leads";

export const saveListingLead = async (data, opts = {}) => {
  try {
    const ref = doc(collection(firestored, LISTING_LEADS_COLLECTION));
    const now = Date.now();
    const lead = {
      ...ensureLeadOwner(data),
      id: ref.id,
      status: data.status || "active",
      stage: data.stage || "New Lead",
      createdAt: now,
      updatedAt: now,
      deletedAt: 0,
    };
    await setDoc(ref, lead);
    // Opt-in sync: a brand-new lead only pushes when its "Sync to Follow Up
    // Boss" switch was flipped on. (Updates still call the backend, which
    // makes the same check against the stored lead.)
    if (!opts.skipFubPush && lead.fubSync === true)
      pushLeadToFub(ref.id, "listing");
    return { success: true, id: ref.id, lead };
  } catch (error) {
    console.error("Error saving listing lead:", error);
    return { success: false, error: error.message };
  }
};

export const updateListingLead = async (id, patch, opts = {}) => {
  try {
    await updateDoc(doc(firestored, LISTING_LEADS_COLLECTION, id), {
      ...patch,
      updatedAt: Date.now(),
    });
    if (!opts.skipFubPush) pushLeadToFub(id, "listing");
    return { success: true, id };
  } catch (error) {
    console.error("Error updating listing lead:", error);
    return { success: false, error: error.message };
  }
};

export const updateListingLeadStage = async (id, newStage, extra = {}) =>
  updateListingLead(id, { stage: newStage, ...extra });

export const deleteListingLead = async (id) => {
  try {
    await updateDoc(doc(firestored, LISTING_LEADS_COLLECTION, id), {
      deletedAt: Date.now(),
    });
    return { success: true };
  } catch (error) {
    console.error("Error deleting listing lead:", error);
    return { success: false, error: error.message };
  }
};

// --- Recently Deleted support for leads ---------------------------------
// Deals have had getDeletedDeals / restoreDeal / permanentlyDeleteDeal since
// the Recently Deleted page shipped; leads never did, so a soft-deleted lead
// could be found but not brought back (2026-08-07, 96 Orange Ave W needed a
// hand-written script). Same `deletedAt` convention: 0/falsy = live.

const deletedFrom = async (table) => {
  const snap = await getDocs(collection(firestored, table));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((r) => Number(r.deletedAt) > 0)
    .sort((a, b) => Number(b.deletedAt || 0) - Number(a.deletedAt || 0));
};

export const getDeletedListingLeads = () => deletedFrom(LISTING_LEADS_COLLECTION);
export const getDeletedBuyerLeads = () => deletedFrom(BUYER_LEADS_COLLECTION);

// Restore clears ONLY deletedAt — stage, notes, agent and status all stay as
// they were, matching how 96 Orange was repaired by hand.
//
// All four return `true` on success. recently-deleted.jsx checks `if (!ok)` on
// the result, so returning setDoc's undefined would report a failure on every
// successful restore.
const setDeletedAt = async (table, id, value) => {
  await setDoc(doc(firestored, table, String(id)), { deletedAt: value }, { merge: true });
  return true;
};

export const restoreListingLead = (id) =>
  setDeletedAt(LISTING_LEADS_COLLECTION, id, 0);

export const restoreBuyerLead = (id) =>
  setDeletedAt(BUYER_LEADS_COLLECTION, id, 0);

export const permanentlyDeleteListingLead = async (id) => {
  await deleteDoc(doc(firestored, LISTING_LEADS_COLLECTION, String(id)));
  return true;
};

export const permanentlyDeleteBuyerLead = async (id) => {
  await deleteDoc(doc(firestored, BUYER_LEADS_COLLECTION, String(id)));
  return true;
};

export const listenListingLeads = (callback) => {
  const q = query(
    collection(firestored, LISTING_LEADS_COLLECTION),
    orderBy("createdAt", "desc"),
  );
  return onSnapshot(q, (snap) => {
    const rows = snap.docs
      .map((d) => {
        const row = { id: d.id, ...d.data() };
        row.propertyAddress = stripCountry(row.propertyAddress);
        return row;
      })
      .filter((l) => !(Number(l.deletedAt) > 0));
    callback(rows);
  });
};

// One-time cleanup (admin button): archive leads + appointments whose property
// is already on the board — they converted, but nobody flipped their status,
// so they linger at the bottom of the Status Sheet and in the pipelines.
// The matching lives in planStatusCleanup (statusSheetData.js — pure, tested).
// With { dryRun: true } it only RETURNS the plan (nothing written) so the
// caller can show the admin exactly what will change and confirm first.
// Every write is a recoverable status flip — nothing is deleted.
export const archiveConvertedRecords = async ({ dryRun = false } = {}) => {
  const fetchAll = async (name) => {
    const snap = await getDocs(query(collection(firestored, name)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };
  const [deals, listingLeads, buyerLeads, appointments] = await Promise.all([
    fetchAll("deals"),
    fetchAll(LISTING_LEADS_COLLECTION),
    fetchAll(BUYER_LEADS_COLLECTION),
    fetchAll("listingAppointments"),
  ]);
  const plan = planStatusCleanup({ deals, listingLeads, buyerLeads, appointments });
  if (dryRun) return { ...plan, applied: false };

  const now = Date.now();
  const results = await Promise.allSettled([
    ...plan.listingLeadsToArchive.map((l) =>
      updateDoc(doc(firestored, LISTING_LEADS_COLLECTION, l.id), {
        status: "converted",
        outcome: "won",
        convertedAt: now,
        archivedBy: "status-cleanup",
      }),
    ),
    ...plan.buyerLeadsToArchive.map((l) =>
      updateDoc(doc(firestored, BUYER_LEADS_COLLECTION, l.id), {
        status: "converted",
        outcome: "won",
        convertedAt: now,
        archivedBy: "status-cleanup",
      }),
    ),
    ...plan.appointmentsToWin.map((a) =>
      updateDoc(doc(firestored, "listingAppointments", a.id), {
        status: "won",
        archivedBy: "status-cleanup",
      }),
    ),
  ]);
  const failed = results.filter((r) => r.status === "rejected").length;
  return { ...plan, applied: true, failed };
};

// Admin button: find duplicate lead cards (same contact, or same name+address
// for listings) and merge each cluster into the survivor the admin picked.
// dryRun returns { clusters } only (nothing written) so the modal can show the
// side-by-side preview. `choices` maps cluster.key -> survivorId | "skip".
// Merges are recoverable: non-survivors are soft-deleted (deletedAt) and stamped
// mergedIntoLeadId so any merge can be traced/undone.
export const mergeDuplicateLeads = async ({ dryRun = false, choices = {} } = {}) => {
  const fetchAll = async (name) => {
    const snap = await getDocs(query(collection(firestored, name)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };
  const [listingLeads, buyerLeads] = await Promise.all([
    fetchAll(LISTING_LEADS_COLLECTION),
    fetchAll(BUYER_LEADS_COLLECTION),
  ]);
  const { clusters } = planDuplicateMerge({ listingLeads, buyerLeads });
  if (dryRun) return { clusters, applied: false };

  const now = Date.now();
  const writes = [];
  let mergedClusters = 0;
  clusters.forEach((cluster) => {
    const choice = choices[cluster.key];
    if (!choice || choice === "skip") return;
    const merged = computeMerge(cluster, choice);
    // Chosen survivor is no longer in this cluster (data shifted since preview) —
    // skip rather than merge around a survivor the admin never picked.
    if (!merged) return;
    const { survivorId, survivorPatch, archivedIds } = merged;
    mergedClusters += 1;
    writes.push(
      updateDoc(doc(firestored, cluster.collection, survivorId), {
        coClients: survivorPatch.coClients,
        notes: survivorPatch.notes,
        dedupMergedAt: now,
      }),
    );
    archivedIds.forEach((id) =>
      writes.push(
        updateDoc(doc(firestored, cluster.collection, id), {
          deletedAt: now,
          deleted: true,
          mergedIntoLeadId: survivorId,
          archivedBy: "duplicate-merge",
        }),
      ),
    );
  });
  const results = await Promise.allSettled(writes);
  const failed = results.filter((r) => r.status === "rejected").length;
  return { clusters, applied: true, mergedClusters, failed };
};

// Graduate a listing (seller) lead into a Deal on the Deals Board at "Pre-Market".
// Mirrors convertBuyerLeadToDeal but seller-side; the listing card then leaves the
// pipeline (status "converted") and a Seller deal appears on the board.
export const convertListingLeadToDeal = async (lead, overrides = {}, user = {}) => {
  try {
    // Lossless graduation: a lead that was moved in from an existing deal
    // carries its origin (sourceDealId). Restore that original deal — full
    // data intact — straight to Pre-Market rather than building a sparser one.
    //
    // Only take the shortcut when that deal is REALLY still there. A dangling
    // pointer used to fall straight through this branch: the stub read failed,
    // was swallowed, and the merge-write below then CREATED the deal out of the
    // patch alone — no address, no client, no leadsource — while the lead was
    // marked converted and left the pipeline. Absent source deal, build the
    // deal from the lead instead (the branch below), which is complete.
    if (lead.sourceDealId && (await dealExists(lead.sourceDealId))) {
      // Merge, never overwrite: the intake stub may hold clients the lead
      // doesn't (and vice versa) — restoring it must keep everyone. A failed
      // stub read degrades to just the lead's clients.
      let stub = null;
      try {
        stub = await getDealById(String(lead.sourceDealId));
      } catch (e) {
        console.error("Couldn't read the source deal for the client merge:", e);
      }
      const patch = {
        deletedAt: 0,
        dealStage: "Pre-Market",
        migratedToLeadId: "",
        // Signed = real board deal — clear the file-request stub markers so it leaves the Pending File Creation queue.
        status: "Approved",
        requestType: "",
        // Link the restored deal back to its lead so Lead History renders.
        sourceLeadId: String(lead.id || ""),
        customers: mergeClientIds(
          stub && stub.customers,
          listClients(lead).map((c) => c.contactId).filter(Boolean),
        ),
      };
      if (lead.notes) patch.notes = lead.notes; // carry any pipeline note edits
      // Target Coming Soon date from the graduation dialog — the Pre-Market
      // column's year sections bucket on this field (preMarketGrouping.js).
      if (overrides.listingAppointment)
        patch.listingAppointment = overrides.listingAppointment;
      // The lead's agent wins on graduation — heals older file-requests that
      // were stamped with their creator instead of the chosen agent.
      if (lead.assignedAgent) {
        patch.agentData = lead.assignedAgent;
        patch.agentName = lead.assignedAgentName || "";
      }
      // A signed listing needs the same checklist a converted buyer gets. The
      // intake stub only ever copied in the Prospect stage (and stamped
      // taskStatusTracker.Prospect, so no later top-up pass revisits it), which
      // left graduated listings sitting on the board holding nothing. Seed off
      // the deal as it will stand AFTER the patch — dealStage decides how far
      // up the checklist runs. buildDealBackfill keys "already has this stage"
      // off the deal's real tasks, so re-running can never duplicate.
      try {
        const template = await getTaskTemplate();
        const backfill = buildDealBackfill({ ...(stub || {}), ...patch }, template || {}, {
          stageOrder: STAGE_ORDER,
          nowIso: new Date().toISOString(),
        });
        if (backfill) {
          patch.tasks = backfill.tasks;
          patch.taskStatusTracker = backfill.taskStatusTracker;
        }
      } catch (e) {
        console.error("Listing graduation: task seeding failed, restoring deal without checklist:", e);
      }
      await updateDealById(String(lead.sourceDealId), patch);
      await updateListingLead(lead.id, {
        status: "converted",
        outcome: "won",
        convertedDealId: String(lead.sourceDealId),
        convertedAt: Date.now(),
      });
      // The signed listing means the appointment was won — log it itself.
      classifyAppointmentForLead(lead, "won");
      return { success: true, dealId: String(lead.sourceDealId) };
    }

    const nowId = Date.now();
    const base = buildDealFromLead(
      lead,
      {
        propertyaddress: lead.propertyAddress || "",
        offerPrice: lead.listPrice || lead.estValue || "",
      },
      { user, nowId },
    );
    const newdata = {
      ...base,
      dealtype: "Seller",
      dealStage: "Pre-Market",
      // A signed listing is a real board deal — NOT a pending file-request
      // stub (the old "fileRequest"/"Pending" shape rendered an empty card).
      status: "Approved",
      requestType: "",
      clientLastName:
        base.clientLastName ||
        (lead.contactName || "").trim().split(/\s+/).slice(-1)[0] ||
        "",
      leadsource: lead.source || "",
      listPrice: lead.listPrice || "",
      suggestedPrice: lead.estValue || "",
      listingDate: lead.appointmentAt || "",
      notes: lead.notes || "",
      ...overrides,
    };
    // buildDealFromLead leaves tasks empty on purpose — seeding is the caller's
    // job (same as convertBuyerLeadToDeal). Without this a graduated listing
    // landed on the board with no checklist at all.
    let seeded = newdata;
    try {
      const template = await getTaskTemplate();
      const backfill = buildDealBackfill(newdata, template || {}, {
        stageOrder: STAGE_ORDER,
        nowIso: new Date().toISOString(),
      });
      if (backfill) seeded = { ...newdata, ...backfill };
    } catch (e) {
      console.error("Listing-lead conversion: task seeding failed, creating deal without checklist:", e);
    }
    const res = await saveitemwithid("deals", String(nowId), seeded);
    // saveitemwithid swallows errors (returns the error object instead of
    // throwing). Only mark the lead converted once the deal is truly saved,
    // so a failed write can never orphan the lead off the board.
    if (res !== "success") {
      throw new Error((res && res.message) || "Failed to create the deal");
    }
    await updateListingLead(lead.id, {
      status: "converted",
        outcome: "won",
      convertedDealId: String(nowId),
      convertedAt: nowId,
    });
    // The signed listing means the appointment was won — log it itself.
    classifyAppointmentForLead(lead, "won");
    return { success: true, dealId: String(nowId), deal: seeded };
  } catch (error) {
    console.error("Error graduating listing lead to deal:", error);
    return { success: false, error: error.message };
  }
};

// Convert a won lead into a Deal in "Under Contract", seeding its checklist the
// same way a new deal does, then mark the lead converted. Deal is created FIRST;
// the lead is only flagged converted on success (no orphaned "converted" lead).
export const convertBuyerLeadToDeal = async (lead, overrides = {}, user = {}) => {
  try {
    // Lossless: a buyer lead moved in from an existing deal restores that
    // original deal (full data) to Under Contract rather than building a new one.
    //
    // Only take the shortcut when that deal REALLY still exists. A dangling
    // pointer (e.g. the stub was purged) would otherwise fall through here, the
    // merge-write below would CREATE the deal from the patch alone — no address,
    // client, agent or leadsource — and the lead would be marked converted and
    // vanish from the pipeline. This mirrors the listing-side fix (96 Orange).
    if (lead.sourceDealId && (await dealExists(lead.sourceDealId))) {
      // Merge, never overwrite: the stub may hold a partner the lead dropped
      // (or the lead gained a co-client after intake) — everyone survives. A
      // failed stub read degrades to just the lead's clients.
      let stub = null;
      try {
        stub = await getDealById(String(lead.sourceDealId));
      } catch (e) {
        console.error("Couldn't read the source deal for the client merge:", e);
      }
      const patch = {
        deletedAt: 0,
        dealStage: "Under Contract",
        migratedToLeadId: "",
        // Signed = real board deal — clear the file-request stub markers so it leaves the Pending File Creation queue.
        status: "Approved",
        requestType: "",
        // Link the restored deal back to its lead so Lead History renders.
        sourceLeadId: String(lead.id || ""),
        customers: mergeClientIds(
          stub && stub.customers,
          listClients(lead).map((c) => c.contactId).filter(Boolean),
        ),
        agentData: lead.assignedAgent || "",
        agentName: lead.assignedAgentName || "",
      };
      if (lead.notes) patch.notes = lead.notes;
      await updateDealById(String(lead.sourceDealId), patch);
      await updateBuyerLead(lead.id, {
        status: "converted",
        outcome: "won",
        convertedDealId: String(lead.sourceDealId),
        convertedAt: Date.now(),
      });
      return { success: true, dealId: String(lead.sourceDealId) };
    }

    const nowId = Date.now();
    const base = buildDealFromLead(lead, overrides, { user, nowId });

    let newdata = base;
    try {
      const template = await getTaskTemplate();
      const backfill = buildDealBackfill(base, template || {}, {
        stageOrder: STAGE_ORDER,
        nowIso: new Date().toISOString(),
      });
      if (backfill) newdata = { ...base, ...backfill };
    } catch (e) {
      console.error("Buyer-lead conversion: task seeding failed, creating deal without checklist:", e);
    }

    await saveitemwithid("deals", String(nowId), newdata);
    await updateBuyerLead(lead.id, {
      status: "converted",
        outcome: "won",
      convertedDealId: String(nowId),
      convertedAt: nowId,
    });
    return { success: true, dealId: String(nowId), deal: newdata };
  } catch (error) {
    console.error("Error converting buyer lead to deal:", error);
    return { success: false, error: error.message };
  }
};

// Create a "file request" deal straight from a pipeline lead (buyer or seller)
// so an agent can request the file while adding the lead — it lands in the
// Pending File Creation queue. Mirrors the fileRequest shape built in
// main-list-request, populated from the lead, with the Prospect checklist seeded.
export const createFileRequestFromLead = async (
  lead = {},
  side = "buyer",
  user = {},
  details = {},
) => {
  try {
    const nowId = Date.now();
    const isSeller = side === "seller";
    const lastName =
      (lead.contactName || "").trim().split(/\s+/).slice(-1)[0] || "";

    const base = {
      status: "Pending",
      requestType: "fileRequest",
      dealtype: isSeller ? "Seller" : "Buyer",
      dealStage: "CMAs",
      customers: lead.contactId ? [lead.contactId] : [],
      clientLastName: lastName,
      agentData: lead.assignedAgent || "",
      agentName: lead.assignedAgentName || "",
      leadsource: lead.leadSource || lead.source || "",
      leadCommission: "",
      propertyaddress: isSeller ? lead.propertyAddress || "" : "",
      propertyLocation: null,
      notes: lead.notes || "",
      createdby: user.id || "",
      createdat: nowId,
      sourceLeadId: lead.id || "",
      // File-request specifics — left for the team to fill when building.
      fileFormat: "",
      fileDeadline: "",
      // Property/transaction type — drives which Client Portal forms the client
      // fills out (formsForRequest). Carried from the lead so a condo/pool/multi-
      // family file-request assembles its tailored sections, not the generic set.
      formType: lead.formType || "",
      // Standard deal fields kept present (blank) so no deals view hits undefined.
      propertyphoto: [],
      propertyType: "",
      financingType: "",
      earnestMoney: "",
      offerPrice: "",
      closingdate: "",
      inspection: "",
      inspectionDeadline: "",
      financingDeadline: "",
      totalcommissionrate: "",
      agentCommission: "",
      agentCommissionamount: "",
      fixedagentcommission: "",
      brokeradminfee: "",
      captype: "",
      captypedescription: "",
      splittype: "TeamSplit",
      splitdescription: "Team Member Split",
      inhousereferral: false,
      teamleadName: "",
      assignedteamlead: "",
      flatfee: "",
      flatfeeamount: "",
      tasks: [],
      taskStatusTracker: {},
    };

    // Request-dialog answers (file format, deadline, trust, disclosure, …)
    // overlay the blanks so the team gets a complete request, not a shell.
    // Whitelisted merge — see utils/fileRequestDetails.js.
    let newdata = applyFileRequestDetails(base, details);
    try {
      const template = await getTaskTemplate();
      const backfill = buildDealBackfill(newdata, template || {}, {
        stageOrder: STAGE_ORDER,
        nowIso: new Date().toISOString(),
      });
      if (backfill) newdata = { ...newdata, ...backfill };
    } catch (e) {
      console.error("File-request task seeding failed, creating without checklist:", e);
    }

    const result = await saveitemwithid("deals", String(nowId), newdata);
    // Hand the saved deal back so callers can feed it to the email/notification
    // helpers without rebuilding the shape.
    return { success: result === "success", dealId: String(nowId), deal: newdata };
  } catch (error) {
    console.error("createFileRequestFromLead failed:", error);
    return { success: false, error: error.message };
  }
};

// One-click Feed post of a buyer's NEEDS (no client name), attributed to the
// agent. Records feedPostId on the lead so the UI can show "Posted".
export const postBuyerNeedToFeed = async (lead, user = {}) => {
  try {
    const agentName =
      [user.firstname, user.lastname].filter(Boolean).join(" ") ||
      user.name ||
      "";
    const text = buildBuyerNeedPost(lead);
    const post = await createPost({
      id: generatePostId(),
      authorId: user.id,
      authorName: agentName,
      authorRole: user.role || "",
      text,
    });
    await updateBuyerLead(lead.id, {
      feedPostId: post.id,
      postedToFeedAt: Date.now(),
    });
    return { success: true, post };
  } catch (error) {
    console.error("Error posting buyer need to feed:", error);
    return { success: false, error: error.message };
  }
};

// Append an activity to a buyer lead, stamp lastContacted (ISO date), and
// optionally set the next follow-up. arrayUnion keeps the append atomic.
// `arrayUnion`/`updateDoc`/`doc`/`BUYER_LEADS_COLLECTION` are already in this file.
export const logBuyerActivity = async (leadId, activity, { nextFollowUp } = {}) => {
  try {
    const patch = {
      activities: arrayUnion(activity),
      lastContacted: new Date(activity.at).toISOString().slice(0, 10),
      updatedAt: Date.now(),
    };
    if (nextFollowUp) patch.nextFollowUp = nextFollowUp;
    await updateDoc(doc(firestored, BUYER_LEADS_COLLECTION, leadId), patch);
    return { success: true };
  } catch (error) {
    console.error("Error logging buyer activity:", error);
    return { success: false, error: error.message };
  }
};

// Snooze a follow-up: push nextFollowUp forward and append a note to the
// timeline, WITHOUT touching lastContacted — a snooze is not a contact, so it
// must not make the lead look like it was just reached.
// Dismiss a due follow-up outright: clears the date (the lead drops off the
// Follow-Ups Due widget) and leaves a breadcrumb on the lead's timeline.
export const dismissBuyerFollowUp = async (leadId, activity) => {
  try {
    await updateDoc(doc(firestored, BUYER_LEADS_COLLECTION, leadId), {
      activities: arrayUnion(activity),
      nextFollowUp: "",
      updatedAt: Date.now(),
    });
    return { success: true };
  } catch (error) {
    console.error("Error dismissing follow-up:", error);
    return { success: false, error: error.message };
  }
};

export const snoozeBuyerLead = async (leadId, activity, nextFollowUp) => {
  try {
    const patch = {
      activities: arrayUnion(activity),
      updatedAt: Date.now(),
    };
    if (nextFollowUp) patch.nextFollowUp = nextFollowUp;
    await updateDoc(doc(firestored, BUYER_LEADS_COLLECTION, leadId), patch);
    return { success: true };
  } catch (error) {
    console.error("Error snoozing buyer lead:", error);
    return { success: false, error: error.message };
  }
};

// Real-time listener
export const listenLeadSources = (callback) => {
  const q = query(
    collection(firestored, "lead_sources"),
    orderBy("createdAt", "desc"),
  );
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    const sortedData = [...data].sort((a, b) =>
      (a.abbreviation || "").localeCompare(b.abbreviation || "", undefined, {
        sensitivity: "base",
      }),
    );
    callback(sortedData);
  });

  return unsubscribe;
};

export const getAllusers = async (table) => {
  const items = await getDocs(query(collection(firestored, table)));

  return items.docs.map((doc) => doc.data());
};
// Google Places appends ", USA" to every address — strip it once at load
// time so every screen shows clean addresses.
const stripCountry = (s) =>
  typeof s === "string" ? s.replace(/,\s*(USA|United States)\b/gi, "") : s;

// The dashboard's File Requests queue.
//
// Deliberately NOT getAlldealforAdmin: that drops every soft-deleted deal, and a
// file request is soft-deleted the instant it's created (the deal is just a
// holder for the Drive folder while the real record lives in a pipeline). Read
// the collection raw and let selectPendingFileRequests decide what belongs.
// Is this property already in the system? Reads FRESH rather than using the
// in-memory deals list, because the duplicate is often minutes old and entered
// by someone else (3180 Cuneen Trail: Joe at 11:19am, Merri at 2:02pm).
//
// Fails OPEN: if the lookup errors we return no matches, so a warning check can
// never be the reason a legitimate request cannot be submitted.
export const findDuplicatesForAddress = async (address, opts = {}) => {
  try {
    if (!address) return [];
    const [dealSnap, leadSnap] = await Promise.all([
      getDocs(query(collection(firestored, "deals"))),
      getDocs(query(collection(firestored, LISTING_LEADS_COLLECTION))),
    ]);
    return findAddressMatches({
      address,
      deals: dealSnap.docs.map((d) => d.data()),
      listingLeads: leadSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
      ...opts,
    });
  } catch (error) {
    console.error("Duplicate address check failed:", error);
    return [];
  }
};

export const getPendingFileRequests = async () => {
  try {
    const items = await getDocs(query(collection(firestored, "deals")));
    const deals = items.docs.map((doc) => {
      const d = doc.data();
      return { ...d, propertyaddress: stripCountry(d.propertyaddress) };
    });
    return selectPendingFileRequests(deals);
  } catch (error) {
    console.error("Error loading pending file requests:", error);
    return [];
  }
};

export const getAlldealforAdmin = async (table, adminid) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      // where("assignedteamlead", "==", adminid)
    ),
  );

  const deals = items.docs
    .map((doc) => {
      const d = doc.data();
      return { ...d, propertyaddress: stripCountry(d.propertyaddress) };
    })
    .filter((d) => !(typeof d.deletedAt === "number" && d.deletedAt > 0))
    .sort((a, b) => (a.closingdate || "").localeCompare(b.closingdate || ""));

  // Auto-archive cancelled deals that have aged out (2+ weeks since cancel, or
  // legacy cancelled deals with no cancelledAt). Fire-and-forget and idempotent
  // — already-archived deals are skipped, so re-running is harmless. We also
  // flip `archived` on the returned objects so the UI hides them immediately
  // without waiting for a refetch.
  if (table === "deals") {
    const due = getDealsToAutoArchive(deals, Date.now());
    if (due.length) {
      const dueSet = new Set(due.map(String));
      deals.forEach((d) => {
        if (dueSet.has(String(d.id ?? d.createdat))) d.archived = true;
      });
      Promise.all(
        due.map((id) =>
          setDealArchived(id, true).catch((e) =>
            console.error("Auto-archive failed for deal", id, e),
          ),
        ),
      );
    }
  }

  return deals;
};

// Deal stages that mean a listing has been won (contract signed, going to market
// or further). "Closed" is treated separately as its own appointment status.
const LISTING_WON_STAGES = [
  "Pre-Market",
  "Coming Soon",
  "Under Contract",
  "Pending",
  "Active",
];

// Stages that count as "listed" — first time a deal hits one of these, we
// stamp listedDate. Pending is excluded (it's the post-UC closing window).
const LISTED_STAGES = ["Pre-Market", "Coming Soon", "Active"];

export const updateDealStageInFirebase = async (
  dealId,
  newStage,
  extraData = {}
) => {
  try {
    const dealRef = doc(firestored, "deals", dealId);

    // Stamp first-time-reached timestamps so cycle-time math works without
    // a backend stage-history table. Only set if not already present.
    const snap = await getDoc(dealRef);
    const existing = snap.exists() ? snap.data() : {};
    const stamps = {};
    const nowIso = new Date().toISOString();
    if (LISTED_STAGES.includes(newStage) && !existing.listedDate) {
      stamps.listedDate = nowIso;
    }
    if (newStage === "Under Contract" && !existing.underContractDate) {
      stamps.underContractDate = nowIso;
    }
    if (newStage === "Closed" && !existing.closedDate) {
      stamps.closedDate = nowIso;
    }
    // Auto-archive clock: stamp when the deal enters "Cancelled" (reset each
    // time so re-cancelling restarts the 2 weeks); clear it when it leaves.
    if (newStage === "Cancelled") {
      stamps.cancelledAt = Date.now();
    } else if (existing.cancelledAt) {
      stamps.cancelledAt = null;
    }

    await updateDoc(dealRef, {
      dealStage: newStage,
      ...stamps,
      ...extraData,
    });

    let newApptStatus = null;
    if (newStage === "Closed") newApptStatus = "closed";
    else if (LISTING_WON_STAGES.includes(newStage)) newApptStatus = "won";

    if (newApptStatus) {
      const apptsSnap = await getDocs(
        query(
          collection(firestored, "listingAppointments"),
          where("dealId", "==", String(dealId)),
        ),
      );
      await Promise.all(
        apptsSnap.docs.map((d) =>
          updateDoc(doc(firestored, "listingAppointments", d.id), {
            status: newApptStatus,
          }),
        ),
      );
    }
  } catch (error) {
    console.error("Error updating deal stage:", error);
  }
};

export const getSourceLead = async () => {
  const snapshot = await getDocs(collection(firestored, "lead_sources"));
  const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  console.log("🔥 Direct Firestore test:", data);
  return data;
};

export const updateIsShowStat = async (userId, value) => {
  try {
    const userRef = doc(firestored, "users", userId);
    await updateDoc(userRef, { isShowStats: value });
    console.log(`✅ isShowStat updated for user ${userId}:`, value);
    return true;
  } catch (error) {
    console.error("❌ Error updating isShowStat:", error);
    return false;
  }
};

export const getUserById = async (id) => {
  const docRef = doc(firestored, "users", id);
  const docSnap = await getDoc(docRef);

  return docSnap.exists() ? docSnap.data() : null;
};

export const getIsShowStat = async (userId) => {
  try {
    const ref = doc(firestored, "users", userId);
    const snap = await getDoc(ref);

    if (snap.exists()) {
      return snap.data()?.isShowStat ?? false;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error fetching isShowStat:", error);
    return false;
  }
};

export const getUsers = async () => {
  const snapshot = await getDocs(collection(firestored, "users"));
  const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  console.log("🔥 Direct Firestore test:", data);
  return data;
};

export const uploadImageToUser = async (imageFile, path) => {
  if (!imageFile) return "";

  const storageRef = ref(storage, `${path}/${Date.now()}-${imageFile.name}`);
  const snapshot = await uploadBytes(storageRef, imageFile);
  const downloadURL = await getDownloadURL(snapshot.ref);

  return downloadURL;
};

// Lets a failed write reach the caller. This used to `.catch()` and RETURN the
// error, so `await saveitemwithkey(...)` never threw and the deal chat's
// try/catch could never fire — a refused write looked identical to a good one.
// Firestore shows a local write before the server confirms it, so the message
// appeared, spun, then silently vanished on rollback with nothing explaining
// why (2026-08-02, Henry: "took a long time to spin and then disappear").
export const saveitemwithkey = async (collection, key, chatdata) => {
  await setDoc(doc(firestored, collection, key), chatdata, { merge: true });
  return "success";
};

export const getAllEmployeeTasks = async (
  table,
  parameter,
  comparison,
  value,
) => {
  const items = await getDocs(
    query(collection(firestored, table), where(parameter, comparison, value)),
  );

  return items.docs.map((doc) => doc.data());
};

export const addChatIdToDeal = async (dealId, chatId) => {
  try {
    const dealRef = doc(firestored, "deals", dealId);

    await updateDoc(dealRef, {
      chatId: chatId,
    });

    console.log("Chat ID added successfully");
  } catch (error) {
    console.error("Error adding chat ID:", error);
  }
};

export const getDealTasks = async (dealId) => {
  const dealRef = doc(firestored, "deals", dealId);
  const snap = await getDoc(dealRef);

  if (snap.exists()) {
    return snap.data().tasks || [];
  }
  return [];
};

export const addTaskToDeal = async (dealId, task) => {
  const dealRef = doc(firestored, "deals", dealId);

  const snap = await getDoc(dealRef);

  let existingTasks = [];

  if (snap.exists()) {
    existingTasks = snap.data().tasks || [];
  }

  const updatedTasks = [...existingTasks, task];

  await setDoc(dealRef, { tasks: updatedTasks }, { merge: true });
};

export const updateDealTasks = async (dealId, tasks) => {
  const ref = doc(firestored, "deals", dealId);

  await setDoc(
    ref,
    { tasks: tasks },
    { merge: true }, // ✅ VERY IMPORTANT
  );
};

// Move an agent's still-open coordinator tasks from oldCoordId to newCoord (minimal shape).
// `deals` is the already-loaded deals array (deal.createdat = doc id, deal.tasks = array).
// Returns the number of tasks moved.
export const moveAgentCoordinatorTasks = async (deals, agentId, oldCoordId, newCoord) => {
  const matches = findAgentCoordinatorTasks(deals, agentId, oldCoordId);
  const byDeal = matches.reduce((acc, m) => {
    (acc[m.dealId] = acc[m.dealId] || []).push(m.task.id);
    return acc;
  }, {});
  let moved = 0;
  for (const dealId of Object.keys(byDeal)) {
    const tasks = await getDealTasks(dealId);
    const updated = tasks.map((t) =>
      byDeal[dealId].includes(t.id) ? { ...t, assignee: newCoord || null } : t,
    );
    await updateDealTasks(dealId, updated);
    moved += byDeal[dealId].length;
  }
  return moved;
};

// One-time backfill: add the template checklist to every ACTIVE, non-archived deal.
// Current stage tasks -> Open, earlier -> Completed; coordinator tasks resolve to
// each deal's agent's coordinator. Idempotent (skips already-tracked stages).
// Returns { updated, skipped }.
export const backfillActiveDeals = async (deals, template, usersById, nowIso) => {
  let updated = 0;
  let skipped = 0;
  for (const deal of deals || []) {
    if (deal.archived || !ACTIVE_STAGES.includes(deal.dealStage)) {
      skipped++;
      continue;
    }
    const agent = usersById[deal.agentData];
    const coordId = agent?.listingCoordinatorId;
    const coord = coordId ? usersById[coordId] : null;
    const coordinator = coord
      ? { id: coord.id, firstname: coord.firstname, lastname: coord.lastname, profilepicture: coord.profilepicture || "", role: coord.role }
      : null;
    const txCoordId = agent?.transactionCoordinatorId;
    const txCoord = txCoordId ? usersById[txCoordId] : null;
    const transactionCoordinator = txCoord
      ? { id: txCoord.id, firstname: txCoord.firstname, lastname: txCoord.lastname, profilepicture: txCoord.profilepicture || "", role: txCoord.role }
      : null;
    const result = buildDealBackfill(deal, template, { coordinator, transactionCoordinator, nowIso });
    if (!result) {
      skipped++;
      continue;
    }
    await updateDoc(doc(firestored, "deals", String(deal.createdat)), {
      tasks: result.tasks,
      taskStatusTracker: result.taskStatusTracker,
    });
    updated++;
  }
  return { updated, skipped };
};

export async function saveNoteToFirebase(dealId, text) {
  // Use setDoc with merge so this works even if the deal doc somehow lacks
  // a `notes` field or was created out-of-band. updateDoc would throw if the
  // doc doesn't exist; merge:true upserts safely.
  const dealRef = doc(firestored, "deals", String(dealId));
  await setDoc(dealRef, { notes: text }, { merge: true });
}

export async function getNoteFromFirebase(dealId) {
  try {
    const dealRef = doc(firestored, "deals", dealId);
    const dealSnap = await getDoc(dealRef);
    if (dealSnap.exists()) {
      return dealSnap.data().notes || ""; // fetch the "notes" key
    } else {
      return "";
    }
  } catch (error) {
    console.error("Error fetching note:", error);
    return "";
  }
}

export const getNotifications = async (userid) => {
  const snapshot = await getDocs(
    query(
      collection(firestored, "notifications"),
      where("assigneded", "==", userid),
      orderBy("notificationid", "desc"),
    ),
  );

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};

export const markNotificationAsRead = async (id) => {
  try {
    const ref = doc(firestored, "notifications", id);
    await updateDoc(ref, {
      read: true,
    });
  } catch (error) {
    console.error("Error updating notification:", error);
  }
};

// Mark a batch of notifications read in one shot (the bell's Mark All Read).
// Chunked at 450 to stay under Firestore's 500-writes-per-batch cap.
export const markAllNotificationsAsRead = async (ids = []) => {
  try {
    for (let i = 0; i < ids.length; i += 450) {
      const batch = writeBatch(firestored);
      for (const id of ids.slice(i, i + 450)) {
        batch.update(doc(firestored, "notifications", String(id)), { read: true });
      }
      await batch.commit();
    }
    return { success: true };
  } catch (error) {
    console.error("Error marking all notifications read:", error);
    return { success: false, error: error.message };
  }
};

const TEMPLATE_DOC = "template";

export const getTaskTemplate = async () => {
  const ref = doc(firestored, "tasktemplate", TEMPLATE_DOC);
  const snap = await getDoc(ref);

  return snap.exists() ? snap.data() : {};
};

export const saveTaskTemplate = async (data) => {
  const ref = doc(firestored, "tasktemplate", TEMPLATE_DOC);
  await setDoc(ref, data);
};

export const addElectronicLockbox = async (data) => {
  const docRef = doc(firestored, "electronic_lockboxes", data.id);

  await setDoc(docRef, {
    ...data,
  });

  return docRef;
};

export const updateElectronicLockbox = async (id, data) => {
  if (!id) {
    throw new Error("Document ID is required for update.");
  }

  const docRef = doc(firestored, "electronic_lockboxes", id);

  await updateDoc(docRef, {
    ...data,
  });

  return docRef;
};

export const getElectronicLockboxes = async () => {
  const snap = await getDocs(collection(firestored, "electronic_lockboxes"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const deleteElectronicLockbox = async (id) => {
  try {
    const docRef = doc(firestored, "electronic_lockboxes", id);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error("Error deleting electronic lockbox:", error);
    throw error;
  }
};

export const addPushButtonLockbox = async (data) => {
  const docRef = doc(firestored, "pushbutton_lockboxes", data.id);

  await setDoc(docRef, {
    ...data,
  });

  return docRef;
};

export const updatePushButtonLockbox = async (id, data) => {
  if (!id) {
    throw new Error("Document ID is required for update.");
  }

  const docRef = doc(firestored, "pushbutton_lockboxes", id);

  await updateDoc(docRef, {
    ...data,
  });

  return docRef;
};

export const deletePushButtonLockbox = async (id) => {
  try {
    const docRef = doc(firestored, "pushbutton_lockboxes", id);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error("Error deleting pushbutton lockbox:", error);
    throw error;
  }
};

export const getPushButtonLockbox = async () => {
  const snap = await getDocs(collection(firestored, "pushbutton_lockboxes"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const addContactToFirebase = async (data) => {
  const docRef = doc(firestored, "sync_contacts", String(data.id));

  await setDoc(docRef, data);

  invalidateContactsCache();
  return docRef;
};

// Per-user contacts cache. An agent can have thousands of contacts, and the
// full list was being re-fetched on every screen/deal-detail open (slow, and it
// caused a spurious auto-save when the late load changed component state). Cache
// the list briefly and reuse it; any contact write clears the cache so changes
// show immediately. Short TTL bounds staleness even if a write path is missed.
const _contactsCache = new Map(); // userId -> { at: number, data: [] }
const CONTACTS_TTL_MS = 60 * 1000;

// Synchronous read of cached contacts (null if absent/stale). Lets callers seed
// initial state without an async round-trip on a warm cache.
export const getCachedContacts = (createdAtId) => {
  const hit = _contactsCache.get(createdAtId);
  if (hit && Date.now() - hit.at < CONTACTS_TTL_MS) return hit.data;
  return null;
};

// Reserved cache key for the team-wide list (never a real user id).
export const TEAM_CONTACTS_CACHE_KEY = "__team__";

// Clear the cache (one user, or all). Call after any contact create/update/delete.
// Clearing one user also clears the team-wide list — it contains that user's rows.
export const invalidateContactsCache = (createdAtId) => {
  if (createdAtId == null) _contactsCache.clear();
  else {
    _contactsCache.delete(createdAtId);
    _contactsCache.delete(TEAM_CONTACTS_CACHE_KEY);
  }
};

// GET all synced contacts (cached for CONTACTS_TTL_MS per user)
export const getAllContactsFromFirebase = async (createdAtId) => {
  const cached = getCachedContacts(createdAtId);
  if (cached) return cached;

  const q = query(
    collection(firestored, "sync_contacts"),
    where("createdBy", "==", createdAtId),
  );

  const snapshot = await getDocs(q);

  const data = snapshot.docs.map((doc) => ({
    ...doc.data(),
    id: doc.id,
  }));

  _contactsCache.set(createdAtId, { at: Date.now(), data });
  return data;
};

// GET every contact in the team account (no createdBy filter). Cached under
// TEAM_CONTACTS_CACHE_KEY — NEVER under a user id, so the per-user Contacts
// page cache can't be poisoned with other agents' rows.
export const getTeamContactsFromFirebase = async () => {
  const cached = getCachedContacts(TEAM_CONTACTS_CACHE_KEY);
  if (cached) return cached;

  const snapshot = await getDocs(collection(firestored, "sync_contacts"));
  const data = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));

  _contactsCache.set(TEAM_CONTACTS_CACHE_KEY, { at: Date.now(), data });
  return data;
};

// Role-aware contact list for form lookups. Eligible staff (see
// canSearchTeamContacts) get the whole team's contacts, with rows they don't
// own tagged `_teamOwnerName` for display. Everyone else — and any team-fetch
// failure — gets today's per-user list. "My book" views (Contacts page,
// texting, FUB import) must keep calling getAllContactsFromFirebase directly.
export const getContactsForUser = async (user) => {
  if (user && canSearchTeamContacts(user)) {
    try {
      const [contacts, users] = await Promise.all([
        getTeamContactsFromFirebase(),
        getUsers(),
      ]);
      return tagTeamContacts(contacts, users, user.id);
    } catch (err) {
      console.error("Team contacts load failed; using own contacts:", err);
    }
  }
  return getAllContactsFromFirebase(user?.id);
};

export const updateContactInFirebase = async (id, data) => {
  const ref = doc(firestored, "sync_contacts", id);
  // Accept arbitrary fields (name/email/phone/notes/etc.) so callers can patch
  // a single field without clobbering the others.
  await updateDoc(ref, { ...data });
  invalidateContactsCache();
};

// ============ CONTACT REFERRALS ============
// The referral link lives in ONE place: on the referred person's contact doc
// (referredByContactId + denormalized referredByName). Written as a direct
// Firestore patch — the /update-contact backend allowlist drops these fields
// from its own payloads, but every backend write is merge:true, so a direct
// patch survives all Google/FUB sync paths. Do NOT route through updateContact.
export const setContactReferrer = async (contactId, referrer) => {
  try {
    await updateContactInFirebase(String(contactId), {
      referredByContactId: referrer ? String(referrer.contactId) : "",
      referredByName: referrer ? referrer.name || "" : "",
    });
    return { success: true };
  } catch (error) {
    console.error("Error setting referrer:", error);
    return { success: false, error: error.message };
  }
};

// Everyone this contact referred (the "down" direction of the referral tree).
export const listReferredContacts = async (contactId) => {
  try {
    const snap = await getDocs(
      query(
        collection(firestored, "sync_contacts"),
        where("referredByContactId", "==", String(contactId)),
      ),
    );
    return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
  } catch (error) {
    console.error("Error listing referred contacts:", error);
    return [];
  }
};

export const deleteContactFromFirebase = async (id) => {
  try {
    if (!id) throw new Error("Contact ID is required");

    const docRef = doc(firestored, "sync_contacts", id);
    await deleteDoc(docRef);

    invalidateContactsCache();
    return true;
  } catch (error) {
    console.error("Delete Error:", error);
    throw error;
  }
};

export const saveContactType = async (data) => {
  try {
    const docRef = doc(collection(firestored, "contact_type"));

    await setDoc(docRef, {
      id: docRef.id,
      abbreviation: data.abbreviation,
      type: data.type,
      clientType: data.selectedOption,
      createdAt: data.userId,
    });

    return { success: true, id: docRef.id }; // ✅ success only after Firestore commit
  } catch (error) {
    console.error("Error saving contact type:", error);
    return { success: false, error: error.message };
  }
};

export const updateContactType = async (id, data) => {
  try {
    // Fetch existing document data
    const docRef = doc(firestored, "contact_type", id);

    await updateDoc(docRef, data);
    return { success: true, id: id };
  } catch (error) {
    console.error("Error updating coontact type:", error);
    return { success: false, error: error.message };
  }
};

export const listenContactType = (callback) => {
  const q = query(
    collection(firestored, "contact_type"),
    orderBy("createdAt", "desc"),
  );
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    const sortedData = [...data].sort((a, b) =>
      (a.abbreviation || "").localeCompare(b.abbreviation || "", undefined, {
        sensitivity: "base",
      }),
    );
    callback(sortedData);
  });

  return unsubscribe;
};

export const deleteContactType = async (id) => {
  try {
    await deleteDoc(doc(firestored, "contact_type", id)); // ✅ wait for Firestore delete
    return { success: true };
  } catch (error) {
    console.error("Error deleting contact type:", error);
    return { success: false, error: error.message };
  }
};

export const getContactTypes = async () => {
  try {
    const snapshot = await getDocs(collection(firestored, "contact_type"));

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error fetching contact types:", error);
    return [];
  }
};

export const updateDealById = async (dealId, updatedData) => {
  try {
    const dealRef = doc(firestored, "deals", String(dealId));

    await setDoc(dealRef, updatedData, { merge: true });

    return true;
  } catch (error) {
    console.error("Error updating deal:", error);
    throw error;
  }
};

// export const updateDealById = async (dealId, updatedData) => {
//   try {
//     if (!dealId) throw new Error("Deal ID is required");

//     const dealRef = doc(firestored, "deals", dealId);

//     await updateDoc(dealRef, {
//       ...updatedData,
//     });

//     return {
//       success: true,
//       message: "Deal updated successfully",
//     };
//   } catch (error) {
//     console.error("Error updating deal:", error);

//     return {
//       success: false,
//       message: error.message,
//     };
//   }
// };

export const updatePushButtonLockboxById = async (id, data) => {
  const ref = doc(firestored, "pushbutton_lockboxes", id);
  await updateDoc(ref, data);
};

export const updateElectronicLockboxById = async (id, data) => {
  const ref = doc(firestored, "electronic_lockboxes", id);
  await updateDoc(ref, data);
};

// Hard-delete a user document (no soft delete / type flag — gone from Firestore).
export const hardDeleteUser = async (id) => {
  if (!id) return false;
  await deleteDoc(doc(firestored, "users", String(id)));
  return true;
};

// Unassign any lockbox (electronic or push-button) whose assignedDealId matches.
// Used when we detect a stale reference (e.g., the deal was deleted).
// Firestore == is type-strict, so we try the dealId as both string and number
// to cover historical data shapes.
export const unassignLockboxesByDealId = async (dealId) => {
  if (!dealId) return 0;
  let count = 0;
  const clear = {
    status: "Available",
    assignedDealId: "",
    assignedDealLabel: "",
  };
  const candidates = [];
  const asNumber = Number(dealId);
  if (!Number.isNaN(asNumber)) candidates.push(asNumber);
  candidates.push(String(dealId));
  const seenIds = new Set();
  for (const collectionName of [
    "electronic_lockboxes",
    "pushbutton_lockboxes",
  ]) {
    for (const value of candidates) {
      try {
        const snap = await getDocs(
          query(
            collection(firestored, collectionName),
            where("assignedDealId", "==", value),
          ),
        );
        for (const d of snap.docs) {
          if (seenIds.has(d.ref.path)) continue;
          seenIds.add(d.ref.path);
          await updateDoc(d.ref, clear);
          count += 1;
        }
      } catch (err) {
        console.error(
          `Failed cleaning ${collectionName} for value ${value}:`,
          err,
        );
      }
    }
  }
  return count;
};

export const getContactsByIds = async (contactIds = []) => {
  // Drop null/empty IDs up front — an empty doc path makes Firestore throw.
  const ids = (contactIds || []).filter(
    (id) => id != null && String(id).trim() !== "",
  );
  if (!ids.length) return [];

  // Fetch each contact independently. One bad/missing ID resolves to null
  // instead of rejecting the whole batch (which would blank out every name).
  const results = await Promise.all(
    ids.map(async (contactId) => {
      try {
        const docRef = doc(
          firestored,
          "sync_contacts",
          String(contactId).trim(),
        );
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          return {
            ...snapshot.data(),
            id: snapshot.id,
            firebaseId: snapshot.id,
          };
        }

        return null;
      } catch (error) {
        console.error("Error fetching contact by ID:", contactId, error);
        return null;
      }
    }),
  );

  return results.filter(Boolean);
};

// ==== Team Directory (shared professionals in the team Google account) ====
//
// "Google is the master": pros live as real Google Contacts in one shared
// account; deal addresses are appended to each contact's Notes. The backend
// Cloud Function (functions-api) holds the shared account's credentials and
// does the Google People API work — the client just calls these endpoints.

const TEAM_DIRECTORY_API =
  "https://us-central1-team-edelstein.cloudfunctions.net/api";

// List the shared account's contacts for the Team Directory page.
export const getTeamDirectory = async () => {
  try {
    const res = await authedFetch(`${TEAM_DIRECTORY_API}/team-directory`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.contacts || [];
  } catch (error) {
    console.error("Error loading team directory:", error);
    return [];
  }
};

// Manually add a professional to the shared Team Directory. Rides the same
// backend upsert the deal-save sync uses (matches by email, then name+phone,
// so re-adding someone never duplicates them). Requires a name plus a phone
// or an email.
export const addToTeamDirectory = async ({
  name,
  role = "",
  company = "",
  phone = "",
  email = "",
  person = null, // optional FULL rich contact — the directory entry then carries everything
}) => {
  const res = await authedFetch(`${TEAM_DIRECTORY_API}/team-directory/log`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      address: "Added to Directory",
      date: Date.now(),
      pros: [{ name, role, company, phone, email, person: person || undefined }],
    }),
  });
  if (!res.ok) throw new Error("Directory save failed");
  return res.json();
};

// Upsert each professional participant on a deal into the shared Google account
// and append this property address to their Notes. Fire-and-forget; the backend
// matches (email → name+phone) and is idempotent (won't duplicate a line).
export const syncParticipantsToDirectory = async (
  participants,
  { address } = {},
) => {
  try {
    const pros = (participants || []).map(normalizePro).filter(isLoggablePro);
    if (!pros.length) return;
    await authedFetch(`${TEAM_DIRECTORY_API}/team-directory/log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ address, date: Date.now(), pros }),
    });
  } catch (error) {
    console.error("Team Directory sync failed:", error);
  }
};

export const getEmailReceipts = async (userId, tabType = "all") => {
  try {
    if (!userId) throw new Error("User ID is required");

    let q;

    // All records
    if (tabType === "all") {
      q = query(
        collection(firestored, "emailrecepts"),
        where("createdby", "==", userId)
      );
    }

    // Install Vendor
    else if (tabType === "installvendor") {
      q = query(
        collection(firestored, "emailrecepts"),
        where("createdby", "==", userId),
        where("type", "==", "install")
      );
    }

    // Destaging Vendor
    else if (tabType === "destagingVendor") {
      q = query(
        collection(firestored, "emailrecepts"),
        where("createdby", "==", userId),
        where("type", "==", "destaging")
      );
    }

    // Other tabs
    else {
      q = query(
        collection(firestored, "emailrecepts"),
        where("createdby", "==", userId),
        where("type", "==", tabType)
      );
    }

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error fetching email receipts:", error);
    return [];
  }
};

// ============ LISTING APPOINTMENTS ============
// Each document represents one listing appointment that an agent went on
// (or has scheduled). Outcome is tracked via the `status` field.

export const getListingAppointments = async () => {
  const snap = await getDocs(query(collection(firestored, "listingAppointments")));
  return snap.docs.map((d) => {
    const a = { id: d.id, ...d.data() };
    a.address = stripCountry(a.address);
    return a;
  });
};

export const saveListingAppointment = async (data) => {
  const id =
    data.id ||
    `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  await setDoc(
    doc(firestored, "listingAppointments", id),
    { ...data, id, createdAt: data.createdAt || new Date().toISOString() },
    { merge: true },
  );
  return id;
};

export const updateListingAppointment = async (id, updates) => {
  await setDoc(
    doc(firestored, "listingAppointments", id),
    updates,
    { merge: true },
  );
};

export const deleteListingAppointment = async (id) => {
  await deleteDoc(doc(firestored, "listingAppointments", id));
};

// When a listing lead's fate is decided, its logged appointment classifies
// itself — graduated to the Deals Board → "won", marked lost → "lost" — so
// the Goals log stays current without anyone updating it by hand (Henry,
// 2026-08-30). Only a PENDING appointment flips: a status a person picked is
// never overwritten, and re-runs can't clobber anything. A miss is silent —
// plenty of leads never had a logged appointment. Never throws; the lead
// write this rides on must not fail because of the log.
export const classifyAppointmentForLead = async (lead, status) => {
  try {
    const appts = await getListingAppointments();
    const pending = (appts || []).filter(
      (a) => !a.status || a.status === "pending",
    );
    const match = findAppointmentForLead(pending, lead);
    if (!match) return { classified: false };
    await updateListingAppointment(match.id, {
      status,
      classifiedBy: "pipeline-sync",
    });
    return { classified: true, appointmentId: match.id };
  } catch (e) {
    console.error("Appointment auto-classify failed (the lead is fine):", e);
    return { classified: false, error: e.message };
  }
};

// ============ TEAM FEED POSTS ============
// Posts are stored in the `posts` collection. Reactions are a sparse map
// of userId → "like" | "dislike". reactionCounts are denormalized so the
// feed can render counts without iterating reactions.
// Note: computeNewReactionState is imported from ./feedHelpers at the top of this file.

const POSTS_COLLECTION = "posts";
const POSTS_PAGE_SIZE = 20;

// Caller can pass an explicit `id` (so callers can upload images to a known
// storage path before the post doc exists). If omitted, one is generated.
export async function createPost({
  id,
  authorId,
  authorName,
  authorRole,
  text,
  imageUrls = [],
}) {
  const postId = id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const post = {
    id: postId,
    authorId,
    authorName: authorName || "",
    authorRole: authorRole || "",
    text: text || "",
    imageUrls: Array.isArray(imageUrls) ? imageUrls : [],
    createdAt: Date.now(),
    updatedAt: null,
    reactions: {},
    reactionCounts: { ...EMPTY_REACTION_COUNTS },
    commentCount: 0,
    deleted: false,
  };
  await setDoc(doc(firestored, POSTS_COLLECTION, postId), post);
  return post;
}

export function generatePostId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// Cursor is `{ createdAt, id }` or null. The `id` tiebreaker prevents skipping
// posts when two share the same createdAt timestamp (e.g., bulk imports).
export async function getPosts({ cursor = null, pageSize = POSTS_PAGE_SIZE } = {}) {
  const constraints = [
    where("deleted", "==", false),
    orderBy("createdAt", "desc"),
    orderBy("id", "asc"),
    limit(pageSize + 1),
  ];
  if (cursor !== null) {
    constraints.splice(3, 0, startAfter(cursor.createdAt, cursor.id));
  }
  const q = query(collection(firestored, POSTS_COLLECTION), ...constraints);
  const snap = await getDocs(q);
  const docs = snap.docs.map((d) => d.data());
  const hasMore = docs.length > pageSize;
  const posts = hasMore ? docs.slice(0, pageSize) : docs;
  const last = posts.length > 0 ? posts[posts.length - 1] : null;
  const nextCursor = last ? { createdAt: last.createdAt, id: last.id } : null;
  return { posts, nextCursor, hasMore };
}

// Count posts created strictly after `timestamp` (ms epoch). Uses an aggregate
// count query so we don't download the documents — cheap enough to poll.
export async function countPostsSince(timestamp) {
  const ts = Number(timestamp) || 0;
  const q = query(
    collection(firestored, POSTS_COLLECTION),
    where("deleted", "==", false),
    where("createdAt", ">", ts),
  );
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

// Stamp "now" as the last time this user opened the Feed, so the unread badge
// resets. Stored on the user doc → consistent across the user's devices.
export async function markFeedSeen(userId) {
  if (!userId) return;
  await updateUserProfile(userId, { lastSeenFeedAt: Date.now() });
}

export async function updatePost(id, updates) {
  await updateDoc(doc(firestored, POSTS_COLLECTION, id), {
    ...updates,
    updatedAt: Date.now(),
  });
}

// Soft-delete: feed queries filter out deleted=true.
export async function deletePost(id) {
  await updateDoc(doc(firestored, POSTS_COLLECTION, id), { deleted: true });
}

// Atomic toggle so two simultaneous clicks don't corrupt counts.
// Returns the updated post.
export async function togglePostReaction(postId, userId, reaction) {
  const postRef = doc(firestored, POSTS_COLLECTION, postId);
  return await runTransaction(firestored, async (tx) => {
    const snap = await tx.get(postRef);
    if (!snap.exists()) throw new Error("Post not found");
    const post = snap.data();
    const prev = post.reactions?.[userId] || null;
    const { newReaction, deltas } = computeNewReactionState(prev, reaction);
    const newReactions = { ...(post.reactions || {}) };
    if (newReaction === null) {
      delete newReactions[userId];
    } else {
      newReactions[userId] = newReaction;
    }
    const newCounts = applyReactionDeltas(post.reactionCounts, deltas);
    tx.update(postRef, {
      reactions: newReactions,
      reactionCounts: newCounts,
    });
    return {
      ...post,
      reactions: newReactions,
      reactionCounts: newCounts,
    };
  });
}

// Upload a single image file for a feed post. Stored under the same
// dealPhotos/ path that property photo uploads use — that path is already
// covered by the deployed storage rules, so this works without needing to
// ship a new `posts/` rule.
export async function uploadPostImage(file, postId) {
  if (!file) throw new Error("No file provided");
  const safeName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
  const path = `dealPhotos/feed/${postId}/${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
}

// ============ TEAM FEED COMMENTS ============
// Comments live in posts/{postId}/comments sub-collection. The parent post's
// commentCount is kept in sync via a transaction.

export async function getCommentsForPost(postId) {
  const q = query(
    collection(firestored, POSTS_COLLECTION, postId, "comments"),
    where("deleted", "==", false),
    orderBy("createdAt", "asc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data());
}

export async function addComment(postId, { authorId, authorName, authorRole, text }) {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const comment = {
    id,
    postId,
    authorId,
    authorName: authorName || "",
    authorRole: authorRole || "",
    text: text || "",
    createdAt: Date.now(),
    updatedAt: null,
    deleted: false,
  };
  const commentRef = doc(firestored, POSTS_COLLECTION, postId, "comments", id);
  const postRef = doc(firestored, POSTS_COLLECTION, postId);
  await runTransaction(firestored, async (tx) => {
    const postSnap = await tx.get(postRef);
    if (!postSnap.exists()) throw new Error("Post not found");
    const post = postSnap.data();
    tx.set(commentRef, comment);
    tx.update(postRef, { commentCount: (post.commentCount || 0) + 1 });
  });
  return comment;
}

export async function updateComment(postId, commentId, updates) {
  await updateDoc(
    doc(firestored, POSTS_COLLECTION, postId, "comments", commentId),
    { ...updates, updatedAt: Date.now() },
  );
}

// Soft-delete; decrement commentCount on the parent post.
export async function deleteComment(postId, commentId) {
  const commentRef = doc(firestored, POSTS_COLLECTION, postId, "comments", commentId);
  const postRef = doc(firestored, POSTS_COLLECTION, postId);
  await runTransaction(firestored, async (tx) => {
    const commentSnap = await tx.get(commentRef);
    if (!commentSnap.exists()) return;
    const comment = commentSnap.data();
    if (comment.deleted) return;
    const postSnap = await tx.get(postRef);
    tx.update(commentRef, { deleted: true });
    if (postSnap.exists()) {
      const post = postSnap.data();
      tx.update(postRef, {
        commentCount: Math.max(0, (post.commentCount || 0) - 1),
      });
    }
  });
}

// ==== Open Houses ====

// Resolves the first customer on a deal into { name, email }. Handles both:
//   - modern shape: customers = ["contactId", ...] (looked up in sync_contacts)
//   - legacy shape: customers = [{ clientName, name?, email?, ... }, ...]
async function resolveSellerFromDeal(deal) {
  const first = deal?.customers?.[0];
  if (!first) return { name: "", email: "" };
  if (typeof first === "object") {
    return {
      name: first.name || first.clientName || "",
      email: first.email || first.clientEmail || "",
    };
  }
  const contacts = await getContactsByIds([first]);
  const c = contacts[0];
  return { name: c?.name || "", email: c?.email || "" };
}

export const createOpenHouse = async ({ deal, fields, userId }) => {
  const ref = doc(collection(firestored, "openHouses"));
  const seller = await resolveSellerFromDeal(deal);
  const lockbox = await resolveLockboxFromDeal(deal);
  const payload = {
    id: ref.id,
    dealId: deal.id,
    propertyAddress: deal.propertyaddress || "",
    // Denormalized listing agent so the open-house list can show an agent the
    // open houses on a listing they own, without a second deal lookup.
    listingAgentId: deal.agentData || "",
    listingAgentName: deal.agentName || "",
    sellerName: seller.name,
    sellerEmail: seller.email,
    date: fields.date,
    startTime: fields.startTime,
    endTime: fields.endTime,
    hostingAgentId: fields.hostingAgentId || "",
    hostingAgentName: fields.hostingAgentName,
    hostingAgentEmail: fields.hostingAgentEmail,
    notes: fields.notes || "",
    // Denormalized lockbox info so the hosting-agent email can show
    // how to get into the property without a second deal lookup.
    lockboxType: lockbox.type,
    lockboxNumber: lockbox.number,
    lockboxCode: lockbox.code,
    status: "Scheduled",
    // True when the home is occupied and we're holding the scheduling
    // notifications until the user confirms the seller verbally OK'd the
    // time. False (or absent) means notifications can / have gone out.
    pendingSellerApproval: !!fields.pendingSellerApproval,
    feedbackToken: generateFeedbackToken(),
    feedback: null,
    pendingRecap: false,
    recapSentAt: null,
    recapSentBy: null,
    cancelReason: null,
    cancelledAt: null,
    cancelledBy: null,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref, payload);
  return { id: ref.id };
};

// Resolve lockbox info from a deal — looks up the actual lockbox doc in
// either pushbutton_lockboxes or electronic_lockboxes by id. Returns
// empty strings when the deal has no lockbox assigned.
async function resolveLockboxFromDeal(deal) {
  const type = deal?.lockboxType || "";
  const lockboxId = deal?.selectedLockbox;
  if (!type || !lockboxId) {
    return { type: "", number: "", code: "" };
  }
  try {
    const collectionName =
      type === "Push Button" ? "pushbutton_lockboxes" : "electronic_lockboxes";
    const snap = await getDoc(doc(firestored, collectionName, String(lockboxId)));
    if (!snap.exists()) return { type, number: "", code: "" };
    const data = snap.data() || {};
    const code = type === "Push Button" ? data.code : data.pin;
    return {
      type,
      number: data.lockboxNumber || "",
      code: code || "",
    };
  } catch (err) {
    console.error("resolveLockboxFromDeal failed:", err);
    return { type, number: "", code: "" };
  }
}

export const updateOpenHouse = async (id, patch, userId) => {
  const ref = doc(firestored, "openHouses", id);
  await updateDoc(ref, {
    ...patch,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
};

export const cancelOpenHouse = async (id, reason, userId) => {
  const ref = doc(firestored, "openHouses", id);
  await updateDoc(ref, {
    status: "Cancelled",
    cancelReason: reason || null,
    cancelledAt: serverTimestamp(),
    cancelledBy: userId,
    updatedAt: serverTimestamp(),
  });
};

export const submitOpenHouseFeedbackInApp = async (id, feedback, userId) => {
  const ref = doc(firestored, "openHouses", id);
  await updateDoc(ref, {
    feedback: {
      ...feedback,
      submittedAt: serverTimestamp(),
      submittedVia: "app",
      submittedByUserId: userId,
    },
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
};

export const deleteOpenHouse = async (id) => {
  await deleteDoc(doc(firestored, "openHouses", id));
};

// Distinct outside hosting agents (not team members) seen on past open
// houses. Used to autocomplete the ScheduleModal name field so the team
// doesn't re-type the same showing agent every time. Returns
// [{ name, email, lastUsedAt }] sorted most-recent first.
export const getOutsideHostingAgents = async () => {
  try {
    const snap = await getDocs(collection(firestored, "openHouses"));
    const seen = new Map(); // key: email lowercased → { name, email, lastUsedAt }
    snap.forEach((d) => {
      const oh = d.data() || {};
      if (oh.hostingAgentId) return; // team member, not outside
      const email = String(oh.hostingAgentEmail || "").trim();
      const name = String(oh.hostingAgentName || "").trim();
      if (!email || !name) return;
      const key = email.toLowerCase();
      const ts = oh.createdAt?.toDate
        ? oh.createdAt.toDate().getTime()
        : (typeof oh.createdAt === "number" ? oh.createdAt : 0);
      const existing = seen.get(key);
      if (!existing || ts > existing.lastUsedAt) {
        seen.set(key, { name, email, lastUsedAt: ts });
      }
    });
    return Array.from(seen.values()).sort((a, b) => b.lastUsedAt - a.lastUsedAt);
  } catch (err) {
    console.error("getOutsideHostingAgents failed:", err);
    return [];
  }
};

export const subscribeOpenHouses = (callback) => {
  const q = query(collection(firestored, "openHouses"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const subscribeOpenHousesForDeal = (dealId, callback) => {
  const q = query(collection(firestored, "openHouses"), where("dealId", "==", dealId));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

// ---- Open House "Up For Grabs" opportunities ----

export const createOpportunity = async ({
  deal,
  suggestedTiming,
  approverLcId,
  userId,
  userName,
}) => {
  const ref = doc(collection(firestored, "openHouseOpportunities"));
  const payload = {
    id: ref.id,
    dealId: deal.id,
    propertyAddress: deal.propertyaddress || "",
    listingAgentId: deal.agentData || "",
    listingAgentName: deal.agentName || "",
    postedBy: userId,
    postedByName: userName || "",
    postedAt: serverTimestamp(),
    suggestedTiming: suggestedTiming || "",
    status: "Open",
    approverLcId: approverLcId || "",
    requests: [],
    declineNote: "",
    resultingOpenHouseId: null,
    cancelledBy: null,
    cancelledAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref, payload);
  return { id: ref.id };
};

// Share an Up For Grabs opportunity to the team feed and remember the post id
// on the opportunity (mirrors the feedPostId-on-lead pattern), so the post
// can be rewritten when the slot is filled or cancelled. Feed problems never
// block the opportunity itself.
export const postUpForGrabsToFeed = async (opportunityId, opp, user = {}) => {
  let post;
  try {
    post = await createPost({
      id: generatePostId(),
      authorId: user.id,
      authorName:
        [user.firstname, user.lastname].filter(Boolean).join(" ") || user.name || "",
      authorRole: user.role || "",
      text: buildUpForGrabsPostText(opp),
    });
    await updateDoc(doc(firestored, "openHouseOpportunities", opportunityId), {
      feedPostId: post.id,
    });
    return { success: true, post };
  } catch (error) {
    console.error("Error sharing Up For Grabs to feed:", error);
    // If the post went live but the opportunity couldn't remember its id, the
    // post would never receive filled/cancelled updates — take it back down.
    if (post?.id) {
      try {
        await deletePost(post.id);
      } catch (cleanupError) {
        console.error("Orphaned Up For Grabs post cleanup failed:", cleanupError);
      }
    }
    return { success: false, error: error.message };
  }
};

// Rewrite the linked feed post (filled/cancelled). Missing post id or a
// deleted post is fine — the feed must never break fill/cancel.
const updateUpForGrabsFeedPost = async (feedPostId, text) => {
  if (!feedPostId) return;
  try {
    await updatePost(feedPostId, { text });
  } catch (error) {
    console.error("Up For Grabs feed post update failed:", error);
  }
};

export const subscribeOpportunities = (callback) => {
  const q = query(collection(firestored, "openHouseOpportunities"));
  return onSnapshot(q, (snap) =>
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  );
};

export const subscribeOpportunitiesForDeal = (dealId, callback) => {
  const q = query(
    collection(firestored, "openHouseOpportunities"),
    where("dealId", "==", dealId),
  );
  return onSnapshot(q, (snap) =>
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  );
};

// Add/replace the requesting agent's pending request. Transactional so two
// agents requesting at once don't clobber each other's entry.
export const addOpportunityRequest = async (opportunityId, request) => {
  const ref = doc(firestored, "openHouseOpportunities", opportunityId);
  await runTransaction(firestored, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Opportunity not found");
    const data = snap.data();
    if (data.status !== "Open") throw new Error("This slot is no longer open.");
    const requests = upsertRequest(data.requests || [], request);
    tx.update(ref, { requests, updatedAt: serverTimestamp() });
  });
};

// Approve a request: create the real open house (host = winner, time = proposed),
// mark the opportunity Filled, stamp the winner Approved + the rest Declined.
// Returns { openHouseId, occupied } so the caller can fire scheduling emails for
// vacant homes (occupied homes hold for seller approval, same as ScheduleModal).
export const approveOpportunityRequest = async ({
  opportunityId,
  winnerRequestId,
  deal,
  userId,
}) => {
  const oppRef = doc(firestored, "openHouseOpportunities", opportunityId);
  const occupied =
    (deal?.occupied || "").toString().trim().toLowerCase() === "yes";

  // Atomically claim the opportunity for this winner BEFORE creating the open
  // house, so two simultaneous approvals can't both produce one. The loser's
  // transaction sees status !== "Open" (or the request no longer Pending) and
  // throws. We capture `winner` from the committed run to build the OH after.
  let winner;
  let oppData;
  await runTransaction(firestored, async (tx) => {
    const snap = await tx.get(oppRef);
    if (!snap.exists()) throw new Error("Opportunity not found");
    const opp = snap.data();
    oppData = opp;
    if (opp.status !== "Open") throw new Error("This slot is no longer open.");
    winner = (opp.requests || []).find((r) => r.id === winnerRequestId);
    if (!winner) throw new Error("Request not found");
    if (winner.status !== "Pending")
      throw new Error("That request is no longer pending.");
    tx.update(oppRef, {
      status: "Filled",
      requests: applyWinner(opp.requests || [], winnerRequestId),
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
  });

  // The slot is committed as Filled from here on, so rewrite the shared feed
  // post NOW — even if creating the open house below fails, the feed must
  // not keep advertising a claimed slot.
  await updateUpForGrabsFeedPost(
    oppData?.feedPostId,
    buildUpForGrabsFilledText(oppData, winner),
  );

  // Claim won — create the real open house and link it back to the opportunity.
  const created = await createOpenHouse({
    deal,
    fields: {
      hostingAgentId: winner.agentId,
      hostingAgentName: winner.agentName,
      hostingAgentEmail: winner.agentEmail || "",
      date: winner.proposedDate,
      startTime: winner.proposedStartTime,
      endTime: winner.proposedEndTime,
      notes: winner.note || "",
      pendingSellerApproval: occupied,
    },
    userId,
  });

  await updateDoc(oppRef, {
    resultingOpenHouseId: created.id,
    updatedAt: serverTimestamp(),
  });

  return { openHouseId: created.id, occupied };
};

export const declineAllOpportunityRequests = async (opportunityId, note, userId) => {
  const ref = doc(firestored, "openHouseOpportunities", opportunityId);
  await runTransaction(firestored, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Opportunity not found");
    const data = snap.data();
    tx.update(ref, {
      requests: declineAll(data.requests || []),
      declineNote: note || "",
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
  });
};

export const cancelOpportunity = async (opportunityId, userId) => {
  const ref = doc(firestored, "openHouseOpportunities", opportunityId);
  let cancelledData;
  await runTransaction(firestored, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    cancelledData = data;
    tx.update(ref, {
      status: "Cancelled",
      requests: declineAll(data.requests || []),
      cancelledBy: userId,
      cancelledAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  // Keep the shared feed post truthful now that the slot is gone.
  if (cancelledData) {
    await updateUpForGrabsFeedPost(
      cancelledData.feedPostId,
      buildUpForGrabsCancelledText(cancelledData),
    );
  }
};

// Sent-email history for one address — the lead drawer's Conversation view.
// One-shot read; emailrecepts docs carry {id: ms timestamp, to, subject,
// body, type, createdby}.
export const getEmailsToAddress = async (email) => {
  const raw = String(email || "").trim();
  if (!raw) return [];
  // Firestore equality is case-sensitive and stored `to` values are as
  // typed — try the address as given and lowercased, dedupe.
  const addrs = [...new Set([raw, raw.toLowerCase()])];
  try {
    const seen = new Map();
    for (const a of addrs) {
      const snap = await getDocs(
        query(collection(firestored, "emailrecepts"), where("to", "==", a)),
      );
      snap.docs.forEach((d) => seen.set(d.id, { ...d.data(), docId: d.id }));
    }
    return [...seen.values()];
  } catch (error) {
    console.error("Error loading email history:", error);
    return [];
  }
};

export const subscribeEmailLog = (callback) => {
  const q = query(
    collection(firestored, "emailLog"),
    orderBy("sentAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const getEligibleListingsForOpenHouse = async () => {
  const snap = await getDocs(collection(firestored, "deals"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// ── GUESSED CLOSING DATES ────────────────────────────────────────────────
// Deals whose closing date the app invented (see utils/closingDateAudit).
// Read-only: this only reports, so the list can be eyeballed before anything
// changes. A genuine same-day closing is indistinguishable from a guess, which
// is exactly why clearing is a separate, explicit second step.
export const auditGuessedClosingDates = async () => {
  const snap = await getDocs(collection(firestored, "deals"));
  const deals = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return {
    scanned: deals.length,
    suspects: auditDealsForGuessedClosingDates(deals),
  };
};

// Blanks the closing date on the given deal ids — and ONLY those ids, which
// come from an audit the human has just reviewed. Each doc is re-checked
// before it's touched, so a deal edited since the audit is skipped rather
// than clobbered.
export const clearGuessedClosingDates = async (ids) => {
  const list = Array.isArray(ids) ? ids : [];
  let cleared = 0;
  let skipped = 0;
  for (const id of list) {
    const ref = doc(firestored, "deals", String(id));
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      skipped++;
      continue;
    }
    if (!looksLikeGuessedClosingDate({ id: snap.id, ...snap.data() })) {
      skipped++;
      continue;
    }
    await updateDoc(ref, { closingdate: "" });
    cleared++;
  }
  return { cleared, skipped };
};

// One-time: stamp listingAgentId onto existing openHouses docs from their deal,
// so the "listings I own" visibility filter works for historical open houses.
// Safe to re-run — skips docs that already have it.
export const backfillOpenHouseListingAgent = async () => {
  const snap = await getDocs(collection(firestored, "openHouses"));
  let updated = 0;
  for (const d of snap.docs) {
    const oh = d.data() || {};
    if (oh.listingAgentId || !oh.dealId) continue;
    const dealSnap = await getDoc(doc(firestored, "deals", String(oh.dealId)));
    if (!dealSnap.exists()) continue;
    const deal = dealSnap.data() || {};
    if (!deal.agentData) continue;
    await updateDoc(d.ref, {
      listingAgentId: deal.agentData,
      listingAgentName: deal.agentName || "",
    });
    updated++;
  }
  return { updated };
};

// One-time cleanup for the "buyer file-requests live in the Buyers Pipeline, not
// the Deals Board" change. Buyer file-requests created before that change are
// `deals` docs sitting in the CMAs column. This flags their originating buyer
// lead as `fileRequested` so the pipeline shows them; the board itself hides
// these cards via a filter (see main-deal-board visibleDeals), so the deal docs
// are left intact (non-destructive). Run once from the console / a dev button.
export const backfillBuyerFileRequestsToPipeline = async () => {
  const snap = await getDocs(collection(firestored, "deals"));
  let flagged = 0;
  let skipped = 0;
  for (const d of snap.docs) {
    const deal = d.data() || {};
    const isBuyerFileRequest =
      deal.requestType === "fileRequest" &&
      deal.dealtype === "Buyer" &&
      deal.dealStage === "CMAs";
    if (!isBuyerFileRequest) continue;
    if (!deal.sourceLeadId) {
      skipped++;
      continue;
    }
    try {
      await updateBuyerLead(
        String(deal.sourceLeadId),
        { fileRequested: true, fileRequestedAt: deal.createdat || Date.now() },
        { skipFubPush: true },
      );
      flagged++;
    } catch (e) {
      console.error("backfill: failed to flag lead", deal.sourceLeadId, e);
      skipped++;
    }
  }
  return { flagged, skipped };
};

// ---- Open House Check-In ---------------------------------------------------
const CHECKINS_COLLECTION = "openHouseCheckins";
const CHECKIN_TEMPLATES_COLLECTION = "openHouseQuestionTemplates";

// Lead lookups for the deal's Lead History panel (deal.sourceLeadId).
export const getBuyerLeadById = async (id) => {
  try {
    const snap = await getDoc(doc(firestored, BUYER_LEADS_COLLECTION, String(id)));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error("Error loading buyer lead:", error);
    return null;
  }
};

export const getListingLeadById = async (id) => {
  try {
    const snap = await getDoc(doc(firestored, LISTING_LEADS_COLLECTION, String(id)));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error("Error loading listing lead:", error);
    return null;
  }
};

export const getOpenHouseById = async (id) => {
  const snap = await getDoc(doc(firestored, "openHouses", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// Quick Check-In: an open house created on the spot from just an address
// (fields built by adHocOpenHouseFields), so agents can run the kiosk for
// open houses that were never put in the scheduler. Mirrors createOpenHouse's
// bookkeeping fields so the list/recap flows treat it like any other row.
export const createAdHocOpenHouse = async (fields, userId) => {
  try {
    const ref = doc(collection(firestored, "openHouses"));
    await setDoc(ref, {
      ...fields,
      id: ref.id,
      lockboxType: "",
      lockboxNumber: "",
      lockboxCode: "",
      pendingSellerApproval: false,
      feedbackToken: generateFeedbackToken(),
      feedback: null,
      pendingRecap: false,
      recapSentAt: null,
      recapSentBy: null,
      cancelReason: null,
      cancelledAt: null,
      cancelledBy: null,
      createdAt: serverTimestamp(),
      createdBy: userId || "",
      updatedAt: serverTimestamp(),
    });
    return { success: true, id: ref.id };
  } catch (error) {
    console.error("Error creating quick open house:", error);
    return { success: false, error: error.message };
  }
};

// One completed kiosk sign-in. Batched with the counter bumps on the
// openHouses doc so totals can never drift from the sign-in docs.
export const saveOpenHouseCheckin = async (data) => {
  try {
    const ref = doc(collection(firestored, CHECKINS_COLLECTION));
    const batch = writeBatch(firestored);
    batch.set(ref, {
      ...data,
      id: ref.id,
      sortedAs: null,
      sortedAt: 0,
      sortedBy: "",
      leadId: "",
      contactId: data.contactId || "",
      createdAt: Date.now(),
    });
    batch.update(doc(firestored, "openHouses", data.openHouseId), {
      checkinPeople: increment(Number(data.partySize) || 1),
      checkinGroups: increment(1),
      checkinCount: increment(1),
    });
    await batch.commit();
    return { success: true, id: ref.id };
  } catch (error) {
    console.error("Error saving open house check-in:", error);
    return { success: false, error: error.message };
  }
};

export const listenOpenHouseCheckins = (openHouseId, callback) => {
  const q = query(
    collection(firestored, CHECKINS_COLLECTION),
    where("openHouseId", "==", openHouseId),
  );
  return onSnapshot(q, (snap) => {
    const rows = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    callback(rows);
  });
};

// The +1 buttons (and their undo) on the traffic counter. Deltas may be
// negative; deriveTraffic clamps displayed totals at zero.
export const bumpOpenHouseTraffic = async (id, { people = 0, groups = 0 }) => {
  try {
    await updateDoc(doc(firestored, "openHouses", id), {
      ...(people ? { manualPeople: increment(people) } : {}),
      ...(groups ? { manualGroups: increment(groups) } : {}),
    });
    return { success: true };
  } catch (error) {
    console.error("Error bumping open house traffic:", error);
    return { success: false, error: error.message };
  }
};

export const markCheckinSorted = async (id, patch) => {
  try {
    await updateDoc(doc(firestored, CHECKINS_COLLECTION, id), {
      ...patch,
      sortedAt: Date.now(),
    });
    return { success: true };
  } catch (error) {
    console.error("Error marking check-in sorted:", error);
    return { success: false, error: error.message };
  }
};

// ---- Per-agent question templates ----
export const saveCheckinTemplate = async (data) => {
  try {
    const ref = doc(collection(firestored, CHECKIN_TEMPLATES_COLLECTION));
    await setDoc(ref, {
      ...data,
      id: ref.id,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      lastUsedAt: 0,
    });
    return { success: true, id: ref.id };
  } catch (error) {
    console.error("Error saving check-in template:", error);
    return { success: false, error: error.message };
  }
};

export const updateCheckinTemplate = async (id, patch) => {
  try {
    await updateDoc(doc(firestored, CHECKIN_TEMPLATES_COLLECTION, id), {
      ...patch,
      updatedAt: Date.now(),
    });
    return { success: true };
  } catch (error) {
    console.error("Error updating check-in template:", error);
    return { success: false, error: error.message };
  }
};

export const deleteCheckinTemplate = async (id) => {
  try {
    await deleteDoc(doc(firestored, CHECKIN_TEMPLATES_COLLECTION, id));
    return { success: true };
  } catch (error) {
    console.error("Error deleting check-in template:", error);
    return { success: false, error: error.message };
  }
};

export const listenCheckinTemplates = (ownerId, callback) => {
  const q = query(
    collection(firestored, CHECKIN_TEMPLATES_COLLECTION),
    where("ownerId", "==", ownerId),
  );
  return onSnapshot(q, (snap) => {
    const rows = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    callback(rows);
  });
};
